import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import imageCompression from 'browser-image-compression';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import io from 'socket.io-client';
// --- ICONS: Added CalendarDays for the new date input ---
import { ChevronLeft, CheckCircle, User, Lock, Shield, LogOut, Users, UserPlus, BarChart2, Play, StopCircle, Award, Image as ImageIcon, QrCode, RefreshCw, Settings, Trash2, Tag, Upload, BookOpen, XCircle, Download, Archive, Edit, Menu, Vote, Maximize, ShieldOff, ShieldCheck, Activity, HeartPulse, Tv2, AlertTriangle, UserCheck, UserX, ArrowLeft, ArrowRight, Building2, FileText, Info, Edit3, CalendarDays, Check, X, Search } from 'lucide-react';

const API_URL = ''; // This MUST be an empty string for Firebase deployment
const socket = io(API_URL || window.location.origin, { path: '/socket.io' });

// --- Core Components (Card, Button, Input, Header) ---
const GlobalStyle = () => (
    <style>{`
        @keyframes spin-text {
            0% { transform: rotateY(0deg); }
            100% { transform: rotateY(360deg); }
        }
        .spin-text-animation {
            display: inline-block;
            animation: spin-text 5s linear infinite;
            transform-style: preserve-3d;
        }
        @keyframes autoscroll {
            from { transform: translateY(0); }
            to { transform: translateY(-50%); }
        }
        .animate-scroll {
            animation: autoscroll var(--scroll-duration, 50s) linear infinite;
        }
    `}</style>
);
const Card = ({ children, className = '' }) => <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }} whileHover={{ y: -5, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)' }} className={`bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8 w-full transition-all duration-300 ${className}`}>{children}</motion.div>;
const Button = ({ children, onClick, className = '', variant = 'primary', type = 'button', disabled = false }) => {
    const variants = { primary: 'bg-zinc-900 text-white hover:bg-black hover:shadow-xl', secondary: 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200 hover:shadow-md', danger: 'bg-red-500 text-white hover:bg-red-600', success: 'bg-green-500 text-white hover:bg-green-600' };
    return <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ type: 'spring', stiffness: 400, damping: 10 }} type={type} onClick={onClick} className={`font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 text-sm uppercase tracking-wider transition-colors ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>{children}</motion.button>;
};

const Input = ({ id, type, placeholder, value, onChange, icon }) => <div className="relative"><span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">{icon}</span><input id={id} type={type} placeholder={placeholder} value={value} onChange={onChange} className="w-full pl-10 pr-4 py-3 bg-slate-100 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500" required /></div>;
const Header = ({ title, onBack, showBack }) => <div className="relative flex items-center justify-center mb-6 h-12">{showBack && <button onClick={onBack} className="absolute left-0 p-2 text-slate-600 hover:text-slate-900"><ChevronLeft size={28} /></button>}<h1 className="text-2xl font-bold text-slate-800">{title}</h1></div>;

const FancyLoader = ({ text }) => (
    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
        <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4 shadow-lg"></div>
        <p className="text-xl font-bold text-slate-700 tracking-wider bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600">
            {text}
        </p>
    </div>
);

// --- Ad Banner Modal Component (Corrected) ---
function AdBannerModal({ desktopBannerUrl, mobileBannerUrl, onClose }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // **FIX APPLIED HERE**
    // This new logic prioritizes the mobile URL if it exists on a mobile device,
    // but crucially falls back to the desktop URL if the mobile one is missing.
    // This ensures a banner always shows if at least one is configured.
    const bannerUrl = (isMobile && mobileBannerUrl) ? mobileBannerUrl : (desktopBannerUrl || mobileBannerUrl);

    useEffect(() => {
        // This effect will now only close the modal if NO valid banner could be found.
        if (!bannerUrl) {
            onClose();
        }
    }, [bannerUrl, onClose]);

    if (!bannerUrl) {
        return null;
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.8, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.8, y: 50 }}
                className="relative bg-white p-4 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col gap-4"
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute -top-3 -right-3 bg-slate-800 text-white rounded-full p-2 z-10 hover:bg-slate-900 transition-colors">
                    <XCircle size={24} />
                </button>
                <div className="flex items-center justify-center gap-2 text-center text-xs text-slate-500">
                    <Info size={14} className="flex-shrink-0" />
                    <p>This advertisement is from the software provider, not a third party.</p>
                </div>
                <div className="flex-grow overflow-hidden rounded-lg">
                    <img src={bannerUrl} alt="Advertisement" className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'}/>
                </div>
            </motion.div>
        </motion.div>
    );
}


// --- API Helper Hook (Updated with polling capability) ---
function useApi(url, pollInterval = 0) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api${url}`, { headers: { 'x-auth-token': token } });
            if (!res.ok) throw new Error('Failed to fetch data');
            const jsonData = await res.json();
            setData(jsonData);
        } catch (err) { setError(err.message); } finally { setLoading(false); }
    };
    
    useEffect(() => {
        fetchData();
        if (pollInterval > 0) {
            const intervalId = setInterval(fetchData, pollInterval);
            return () => clearInterval(intervalId);
        }
    }, [url, pollInterval]);
    
    return { data, loading, error, refetch: fetchData };
}

// --- Login Screen ---

// --- Public Landing Page ---

function DynamicAds() {
    const { data: adsData } = useApi('/ads', 10000);
    const [adIndex, setAdIndex] = useState(0);
    const [showPopup, setShowPopup] = useState(true);

    const bannerAds = adsData ? adsData.filter(a => a.type === 'banner' && a.isActive) : [];
    const featuredAds = adsData ? adsData.filter(a => a.type === 'featured' && a.isActive) : [];

    useEffect(() => {
        if (bannerAds.length > 1) {
            const timer = setInterval(() => {
                setAdIndex(prev => (prev + 1) % bannerAds.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [bannerAds.length]);

    useEffect(() => {
        const hasSeenPopup = sessionStorage.getItem('hasSeenFeaturedAd');
        if (hasSeenPopup) setShowPopup(false);
    }, []);

    const dismissPopup = () => {
        setShowPopup(false);
        sessionStorage.setItem('hasSeenFeaturedAd', 'true');
    };

    return (
        <>
            {bannerAds.length > 0 && (
                <div className="w-full bg-white border-t border-slate-200 p-3 text-center mt-auto shadow-[0_-4px_10px_rgba(0,0,0,0.05)] overflow-hidden relative h-48 flex items-center justify-center z-40">
                    <p className="absolute top-2 left-1/2 -translate-x-1/2 text-slate-400 text-[10px] uppercase tracking-widest font-bold z-10">Sponsored Advertisement</p>
                    <AnimatePresence mode="wait">
                        <motion.a 
                            key={bannerAds[adIndex]._id}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5 }}
                            href={bannerAds[adIndex].targetUrl.match(/^https?:\/\//) ? bannerAds[adIndex].targetUrl : 'https://' + bannerAds[adIndex].targetUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-block transition-transform hover:scale-[1.02] absolute w-full h-full p-6 flex justify-center"
                        >
                            <img src={bannerAds[adIndex].imageUrl} alt="Advertisement" className="max-h-full max-w-full object-contain rounded-xl shadow-md" />
                        </motion.a>
                    </AnimatePresence>
                </div>
            )}
            {showPopup && featuredAds.length > 0 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-2 overflow-hidden">
                        <button onClick={dismissPopup} className="absolute top-4 right-4 bg-slate-900 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold shadow hover:bg-slate-700 z-10">&times;</button>
                        <a href={featuredAds[0].targetUrl.match(/^https?:\/\//) ? featuredAds[0].targetUrl : 'https://' + featuredAds[0].targetUrl} target="_blank" rel="noopener noreferrer" onClick={dismissPopup}>
                            <img src={featuredAds[0].imageUrl} className="w-full h-auto max-h-[70vh] object-contain rounded-2xl" alt="Featured Sponsor" />
                        </a>
                    </motion.div>
                </div>
            )}
        </>
    );
}
// --- DynamicAds ---

function LandingPage({ electionConfig }) {
    const [showPinModal, setShowPinModal] = useState(false);
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [verifying, setVerifying] = useState(false);

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        setVerifying(true);
        setPinError('');
        try {
            const res = await fetch('/api/auth/verify-pin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                navigate('/login');
            } else {
                setPinError(data.msg || 'Invalid PIN');
            }
        } catch (err) {
            setPinError('Network Error. Is the server running?');
        }
        setVerifying(false);
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col relative overflow-hidden font-sans text-slate-100">
            {/* Background Gradients */}
            <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-indigo-600/30 blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-fuchsia-600/30 blur-[120px] pointer-events-none"></div>

            {/* Header */}
            <header className="relative z-10 py-6 px-8 flex justify-between items-center border-b border-white/10 bg-white/5 backdrop-blur-md">
                <div className="flex items-center gap-4">
                    {electionConfig?.logoUrl && <img src={electionConfig.logoUrl} alt="Logo" className="h-10 object-contain" />}
                    <h1 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-300 to-fuchsia-300 bg-clip-text text-transparent">
                        {electionConfig?.appTitle || 'CampusVote'}
                    </h1>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-16 text-center">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <h2 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight leading-tight">
                        Elevating Campus <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-fuchsia-400">Democracy</span>
                    </h2>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-16 leading-relaxed">
                        Your voice matters. Vote for a better campus experience with our secure and transparent system.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full">
                    {/* Card 1: Results */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col items-center text-center hover:bg-white/10 transition-all duration-300 cursor-pointer group"
                        onClick={() => navigate('/public')}
                    >
                        <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <BarChart2 size={32} />
                        </div>
                        <h3 className="text-2xl font-bold mb-3">Live Results</h3>
                        <p className="text-slate-400 mb-8 flex-1">View real-time charts and dynamic results overview of the ongoing elections.</p>
                        <button className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 font-semibold transition-colors">View Results</button>
                    </motion.div>

                    {/* Card 2: Nominate */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                        className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col items-center text-center hover:bg-white/10 transition-all duration-300 cursor-pointer group"
                        onClick={() => navigate('/apply')}
                    >
                        <div className="w-16 h-16 rounded-2xl bg-fuchsia-500/20 text-fuchsia-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <UserPlus size={32} />
                        </div>
                        <h3 className="text-2xl font-bold mb-3">Submit Nomination</h3>
                        <p className="text-slate-400 mb-8 flex-1">Application form to submit your candidacy for the upcoming campus elections.</p>
                        <button className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 font-semibold transition-colors">Apply Now</button>
                    </motion.div>

                    {/* Card 3: Staff Portal */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
                        className="bg-gradient-to-br from-indigo-600/20 to-fuchsia-600/20 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col items-center text-center hover:border-white/30 transition-all duration-300 cursor-pointer group relative overflow-hidden"
                        onClick={() => setShowPinModal(true)}
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative z-10 w-16 h-16 rounded-2xl bg-white/10 text-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Shield size={32} />
                        </div>
                        <h3 className="relative z-10 text-2xl font-bold mb-3">Staff Portal</h3>
                        <p className="relative z-10 text-slate-400 mb-8 flex-1">Secure dashboard for administrators and election monitors.</p>
                        <button className="relative z-10 w-full py-3 rounded-xl bg-white text-slate-900 font-bold hover:bg-slate-200 transition-colors">Staff Login</button>
                    </motion.div>
                </div>
            </main>

            <footer className="relative z-10 py-6 text-center text-slate-500 text-sm border-t border-white/10 bg-white/5 backdrop-blur-md flex flex-col gap-1">
                <span>&copy; {new Date().getFullYear()} {electionConfig?.appTitle || 'Finelectra'}. All rights reserved.</span>
                <span className="text-xs text-slate-400/80 font-semibold mt-1 tracking-wider uppercase">Platform developed by <a href="https://finixia.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Finixia Dedecons</a></span>
            </footer>

            <DynamicAds />
            {/* PIN Modal */}
            <AnimatePresence>
                {showPinModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-slate-900 border border-slate-700 rounded-3xl p-8 max-w-sm w-full shadow-2xl relative">
                            <button onClick={() => setShowPinModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20}/></button>
                            <div className="w-12 h-12 bg-indigo-500/20 text-indigo-400 rounded-xl flex items-center justify-center mb-6 mx-auto">
                                <Lock size={24} />
                            </div>
                            <h3 className="text-2xl font-bold text-center mb-2">Security Check</h3>
                            <p className="text-slate-400 text-center text-sm mb-6">Enter the Staff Portal PIN to proceed.</p>
                            
                            <form onSubmit={handlePinSubmit}>
                                <input type="password" placeholder="Enter PIN" value={pin} onChange={e => setPin(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-center text-xl tracking-[0.5em] mb-4 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" autoFocus />
                                {pinError && <p className="text-red-400 text-sm text-center mb-4">{pinError}</p>}
                                <button type="submit" disabled={verifying} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl transition-colors">
                                    {verifying ? 'Verifying...' : 'Access Portal'}
                                </button>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

function LoginScreen({ onLogin, logoUrl, secondaryLogoUrl, appName }) { 
    const [role, setRole] = useState('voter');
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setIsLoading(true);
        const url = role === 'voter' ? `${API_URL}/api/auth/voter-login` : `${API_URL}/api/auth/admin-login`;
        const body = role === 'voter' ? { voterId: identifier, password } : { username: identifier, password };
        try {
            const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.msg || 'Login failed');
            localStorage.setItem('token', data.token);
            onLogin(data.user);
        } catch (err) { setError(err.message); } finally { setIsLoading(false); }
    };
    return <div className="max-w-md mx-auto"><Card>
        <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">{appName ? `${appName} Portal` : 'Finelectra Portal'}</h1>
            <div className="flex justify-center items-center gap-4 h-12 mb-2">
                <img src={logoUrl || 'https://placehold.co/150x50/E0E0E0/333?text=Logo+1'} alt="Election Logo" className="h-full w-auto object-contain" onError={(e) => e.target.style.display='none'} />
                {secondaryLogoUrl && <img src={secondaryLogoUrl} alt="Secondary Logo" className="h-full w-auto object-contain" onError={(e) => e.target.style.display='none'} />}
            </div>
            <p className="text-slate-500 mt-2">Please sign in to continue</p>
        </div>
        <div className="flex bg-slate-200 rounded-lg p-1 mb-6">
            <button onClick={() => setRole('voter')} className={`w-1/2 p-2 rounded-md font-semibold transition-colors ${role === 'voter' ? 'bg-white shadow' : 'text-slate-600'}`}>Voter / Monitor</button>
            <button onClick={() => setRole('admin')} className={`w-1/2 p-2 rounded-md font-semibold transition-colors ${role === 'admin' ? 'bg-white shadow' : 'text-slate-600'}`}>Admin</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
            <Input id="identifier" type="text" placeholder={role === 'voter' ? 'Voter ID' : 'Admin Username'} value={identifier} onChange={(e) => setIdentifier(e.target.value)} icon={role === 'voter' ? <User size={20} /> : <Shield size={20} />} />
            <Input id="password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} icon={<Lock size={20} />} />
            {error && <p className="text-red-500 text-sm text-center pt-2">{error}</p>}
            <div className="pt-4"><Button type="submit" variant="primary" className="w-full" disabled={isLoading}>{isLoading ? 'Signing In...' : 'Sign In'}</Button></div>
        </form>
        
        
    </Card></div>;
}


// --- Admin Dashboard Components ---

function AuditLogDashboard() {
    const { data: logs, loading } = useApi('/admin/audit-logs', 10000);
    
    if (loading && !logs) return <Card><p className="text-center">Loading Audit Logs...</p></Card>;
    
    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Audit & Security Logs</h2>
            <Card>
                <div className="max-h-[600px] overflow-y-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-slate-700 uppercase bg-slate-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-3">Timestamp</th>
                                <th className="px-4 py-3">Action</th>
                                <th className="px-4 py-3">Details</th>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(logs || []).map(log => (
                                <tr key={log._id} className="border-b hover:bg-slate-50">
                                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                                    <td className="px-4 py-2 font-semibold text-slate-700">{log.action}</td>
                                    <td className="px-4 py-2 text-slate-600">{log.details}</td>
                                    <td className="px-4 py-2 text-slate-700">{log.user}</td>
                                    <td className="px-4 py-2 text-slate-500">{log.ip}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

const AlertPopup = ({ message, icon, color }) => (
    <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.3 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
        className={`fixed bottom-5 right-5 z-50 p-4 rounded-lg shadow-2xl flex items-center gap-3 ${color}`}
    >
        {icon}
        <span className="font-semibold">{message}</span>
    </motion.div>
);

function LiveDashboard() {
    const { data: stats, loading, refetch } = useApi('/admin/live-stats', 5000);

    useEffect(() => {
        socket.on('newVote', () => {
            refetch();
        });
        return () => {
            socket.off('newVote');
        };
    }, [refetch]);
    const [alerts, setAlerts] = useState([]);
    const prevMonitorsRef = useRef([]);

    useEffect(() => {
        if (stats) {
            const currentMonitors = stats.activeMonitors || [];
            const prevMonitors = prevMonitorsRef.current;
            const offlineMonitors = prevMonitors.filter(m => !currentMonitors.includes(m));
            offlineMonitors.forEach(monitorName => {
                addAlert(`${monitorName} went offline!`, <UserX size={20} />, 'bg-yellow-400 text-yellow-900');
            });
            const onlineMonitors = currentMonitors.filter(m => !prevMonitors.includes(m));
            onlineMonitors.forEach(monitorName => {
                addAlert(`${monitorName} is online.`, <UserCheck size={20} />, 'bg-green-400 text-green-900');
            });
            (stats.newInvalidScans || []).forEach(scan => {
                addAlert(`Invalid QR scan by ${scan.monitorName}!`, <AlertTriangle size={20} />, 'bg-red-500 text-white');
            });
            (stats.newUsedScans || []).forEach(scan => {
                addAlert(`Used QR scan by ${scan.monitorName}!`, <AlertTriangle size={20} />, 'bg-orange-500 text-white');
            });
            prevMonitorsRef.current = currentMonitors;
        }
    }, [stats]);

    const addAlert = (message, icon, color) => {
        const id = Date.now();
        setAlerts(prev => [...prev, { id, message, icon, color }]);
        setTimeout(() => {
            setAlerts(prev => prev.filter(a => a.id !== id));
        }, 5000);
    };

    if (loading && !stats) return <Card><p className="text-center">Loading Live Data...</p></Card>;

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Live Election Dashboard</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="text-center">
                    <Vote size={40} className="mx-auto text-slate-500 mb-3" />
                    <p className="text-5xl font-bold">{stats?.totalVotes ?? 0}</p>
                    <p className="text-slate-600 font-semibold">Total Votes Cast</p>
                </Card>
                <Card className="text-center">
                    <HeartPulse size={40} className="mx-auto text-slate-500 mb-3" />
                    <p className="text-5xl font-bold">{stats?.activeMonitors?.length ?? 0}</p>
                    <p className="text-slate-600 font-semibold">Active Monitors</p>
                </Card>
                <Card className="text-center">
                    <Activity size={40} className="mx-auto text-slate-500 mb-3" />
                    <p className="text-2xl font-bold text-green-600">SYSTEM STABLE</p>
                    <p className="text-slate-600 font-semibold">Status</p>
                </Card>
            </div>
            <div className="mt-6">
                <Card>
                    <h3 className="text-lg font-bold mb-3">Active Monitor Stations</h3>
                    {stats?.activeMonitors && stats.activeMonitors.length > 0 ? (
                        <ul className="space-y-2">
                            {stats.activeMonitors.map(name => (
                                <li key={name} className="flex items-center gap-3 p-2 bg-green-50 text-green-800 rounded-lg">
                                    <UserCheck size={18} /> <span className="font-semibold">{name}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-slate-500">No monitors are currently active.</p>
                    )}
                </Card>
            </div>
             <AnimatePresence>
                {alerts.map(alert => (
                    <AlertPopup key={alert.id} {...alert} />
                ))}
            </AnimatePresence>
        </div>
    );
}

function MonitorTracking() {
    const { data, loading } = useApi('/admin/monitor-details', 10000);

    if (loading && !data) return <Card><p className="text-center">Loading Monitor Details...</p></Card>;

    const getStatusChip = (status) => {
        switch (status) {
            case 'valid': return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">Valid</span>;
            case 'invalid_token': return <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">Invalid</span>;
            case 'already_used': return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full">Used</span>;
            default: return null;
        }
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Monitor Tracking</h2>
            <div className="mb-6">
                <Card>
                    <h3 className="text-lg font-bold mb-3">Active Monitor Stations ({data?.activeMonitors?.length ?? 0})</h3>
                    {data?.activeMonitors && data.activeMonitors.length > 0 ? (
                        <ul className="space-y-2">
                            {data.activeMonitors.map(name => (
                                <li key={name} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                                    <UserCheck size={18} className="text-green-600"/> <span className="font-semibold">{name}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-slate-500">No monitors are currently active.</p>
                    )}
                </Card>
            </div>
            <Card>
                <h3 className="text-lg font-bold mb-3">Recent Scan Activity (Last 100)</h3>
                <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-slate-700 uppercase bg-slate-50 sticky top-0">
                            <tr>
                                <th scope="col" className="px-4 py-3">Monitor</th>
                                <th scope="col" className="px-4 py-3">Status</th>
                                <th scope="col" className="px-4 py-3">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.scanLogs || []).map(log => (
                                <tr key={log._id} className="border-b">
                                    <td className="px-4 py-2 font-medium">{log.monitorName}</td>
                                    <td className="px-4 py-2">{getStatusChip(log.status)}</td>
                                    <td className="px-4 py-2 text-slate-500">{new Date(log.timestamp).toLocaleTimeString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

function ManageVoters() {
    const { data: voters, loading, refetch } = useApi('/admin/voters');
    const [voterID, setVoterID] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [editingVoter, setEditingVoter] = useState(null);

    const resetForm = () => {
        setVoterID('');
        setName('');
        setPassword('');
        setEditingVoter(null);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        const url = editingVoter 
            ? `${API_URL}/api/admin/voters/${editingVoter._id}` 
            : `${API_URL}/api/admin/voters`;
        const method = editingVoter ? 'PUT' : 'POST';
        
        const body = { voterID, name };
        if (password) {
            body.password = password;
        }

        await fetch(url, { 
            method, 
            headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, 
            body: JSON.stringify(body) 
        });
        
        resetForm();
        refetch();
    };

    const handleEditClick = (voter) => {
        setEditingVoter(voter);
        setVoterID(voter.voterID);
        setName(voter.name);
        setPassword('');
    };
    
    const handleDeleteClick = async (voterId) => {
        if (window.confirm("Are you sure you want to delete this voter?")) {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/admin/voters/${voterId}`, {
                method: 'DELETE',
                headers: { 'x-auth-token': token }
            });
            refetch();
        }
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">{editingVoter ? 'Edit Voter' : 'Add Voter'}</h2>
            <Card className="mb-6">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    <Input id="voterID" placeholder="Voter ID" value={voterID} onChange={e => setVoterID(e.target.value)} icon={<User size={18}/>} />
                    <Input id="name" placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} icon={<User size={18}/>} />
                    <Input id="password" type="password" placeholder={editingVoter ? "New Password (optional)" : "Password"} value={password} onChange={e => setPassword(e.target.value)} icon={<Lock size={18}/>} />
                    <div className="flex gap-4">
                        <Button className="w-full" type="submit" variant={editingVoter ? 'success' : 'primary'}>
                            {editingVoter ? 'Update Voter' : 'Add Voter'}
                        </Button>
                        {editingVoter && <Button className="w-full" variant="secondary" onClick={resetForm}>Cancel</Button>}
                    </div>
                </form>
            </Card>
            <Card>
                <h3 className="text-lg font-bold mb-3">Existing Voters</h3>
                {loading && <p>Loading voters...</p>}
                <ul className="space-y-2 max-h-60 overflow-y-auto">
                    {(voters || []).map(v => (
                        <li key={v._id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                            <span>{v.name} ({v.voterID})</span>
                            <div className="flex gap-2">
                                <button onClick={() => handleEditClick(v)} className="p-1 text-slate-600 hover:text-slate-900"><Edit size={16}/></button>
                                <button onClick={() => handleDeleteClick(v._id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 size={16}/></button>
                            </div>
                        </li>
                    ))}
                </ul>
            </Card>
        </div>
    );
}

function ManageCandidates({ positions }) {
    const { data: candidates, loading, refetch } = useApi('/admin/candidates');
    const [name, setName] = useState('');
    const [photo, setPhoto] = useState('');
    const [position, setPosition] = useState('');
    const [editingCandidate, setEditingCandidate] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => { if (positions.length > 0 && !position) setPosition(positions[0].name); }, [positions, position]);
    
    const resetForm = () => {
        setName('');
        setPhoto('');
        if (positions.length > 0) setPosition(positions[0].name);
        setEditingCandidate(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setPhoto(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        const url = editingCandidate 
            ? `${API_URL}/api/admin/candidates/${editingCandidate._id}` 
            : `${API_URL}/api/admin/candidates`;
        const method = editingCandidate ? 'PUT' : 'POST';
        
        const body = { name, position };
        if (photo && photo.startsWith('data:image')) {
            body.photo = photo;
        }

        await fetch(url, { 
            method, 
            headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, 
            body: JSON.stringify(body) 
        });
        
        resetForm();
        refetch();
    };

    const handleEditClick = (candidate) => {
        setEditingCandidate(candidate);
        setName(candidate.name);
        setPosition(candidate.position);
        setPhoto(candidate.photo);
    };
    
    const handleDeleteClick = async (candidateId) => {
        if (window.confirm("Are you sure you want to delete this candidate?")) {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/admin/candidates/${candidateId}`, {
                method: 'DELETE',
                headers: { 'x-auth-token': token }
            });
            refetch();
        }
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">{editingCandidate ? 'Edit Candidate' : 'Add Candidate'}</h2>
            <Card className="mb-6">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    <Input id="name" placeholder="Candidate Name" value={name} onChange={e => setName(e.target.value)} icon={<User size={18}/>} />
                    <div className="relative">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400"><Award size={18}/></span>
                        <select id="position" value={position} onChange={e => setPosition(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-slate-100 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                            {(positions || []).map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="relative">
                        <label htmlFor="photo-upload" className="w-full font-bold py-3 px-4 rounded-lg transition-all transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-slate-500/50 flex items-center justify-center gap-2 text-sm uppercase tracking-wider bg-slate-200 text-slate-800 hover:bg-slate-300 cursor-pointer">
                            <Upload size={16}/> {photo ? 'Change Photo' : 'Upload Photo'}
                        </label>
                        <input id="photo-upload" type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} className="hidden" />
                    </div>
                    {photo && !photo.startsWith('data:image') && <img src={photo} alt="Current" className="w-24 h-24 rounded-full mx-auto object-cover"/>}
                    <div className="flex gap-4">
                        <Button className="w-full" type="submit" variant={editingCandidate ? 'success' : 'primary'}>
                            {editingCandidate ? 'Update Candidate' : 'Add Candidate'}
                        </Button>
                        {editingCandidate && <Button className="w-full" variant="secondary" onClick={resetForm}>Cancel</Button>}
                    </div>
                </form>
            </Card>
            <Card>
                <h3 className="text-lg font-bold mb-3">Existing Candidates</h3>
                {loading && <p>Loading candidates...</p>}
                <ul className="space-y-2 max-h-60 overflow-y-auto">
                    {(candidates || []).map(c => (
                        <li key={c._id} className="flex items-center justify-between gap-4 p-2 bg-slate-50 rounded">
                            <div className="flex items-center gap-4">
                                <img src={c.photo || `https://placehold.co/40x40/E0E0E0/333?text=${c.name.charAt(0)}`} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                                <div>
                                    <p className="font-semibold">{c.name}</p>
                                    <p className="text-sm text-slate-500 capitalize">{c.position}</p>
                                </div>
                            </div>
                             <div className="flex gap-2">
                                <button onClick={() => handleEditClick(c)} className="p-1 text-slate-600 hover:text-slate-900"><Edit size={16}/></button>
                                <button onClick={() => handleDeleteClick(c._id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 size={16}/></button>
                            </div>
                        </li>
                    ))}
                </ul>
            </Card>
        </div>
    );
}

function AnimatedBar({ value, maxValue }) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
        requestAnimationFrame(() => setWidth(percentage));
    }, [value, maxValue]);
    return <div className="w-full bg-slate-200 rounded-full h-4"><div className="bg-slate-600 h-4 rounded-full transition-all duration-1000 ease-out" style={{ width: `${width}%` }}></div></div>;
}
function ViewResults({ electionStatus, electionConfig }) {
    const { data: results, loading } = useApi('/admin/results');
    const [viewState, setViewState] = useState('idle');
    const [countdown, setCountdown] = useState(20);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAdVisible, setIsAdVisible] = useState(false);

    useEffect(() => {
        let timer;
        if (viewState === 'branding') {
            timer = setTimeout(() => setViewState('countdown'), 5000);
        } else if (viewState === 'countdown') {
            if (countdown > 0) {
                timer = setInterval(() => setCountdown(prev => prev - 1), 1000);
            } else {
                setViewState('results');
            }
        }
        return () => { clearTimeout(timer); clearInterval(timer); };
    }, [viewState, countdown]);
    
    const handleShowResults = () => {
        if (electionConfig?.desktopBannerUrl || electionConfig?.mobileBannerUrl) {
            setIsAdVisible(true);
        } else {
            setViewState('branding');
        }
    };

    const handleAdClose = () => {
        setIsAdVisible(false);
        setViewState('branding');
        setCountdown(20);
        setCurrentSlide(0);
    };


    if (!electionStatus?.resultsPublished) {
        return <Card><p className="text-center font-semibold">Results have not been published yet.</p></Card>;
    }
    
    if (viewState === 'idle') {
        return <Card><Button className="w-full" onClick={handleShowResults}><BarChart2 size={16}/> Show Results</Button></Card>;
    }

    const groupedResults = (results || []).reduce((acc, candidate) => {
        (acc[candidate.position] = acc[candidate.position] || []).push(candidate);
        return acc;
    }, {});
    for (const position in groupedResults) {
        groupedResults[position].sort((a, b) => b.votes - a.votes);
    }
    const positions = Object.keys(groupedResults);
    const currentPositionData = groupedResults[positions[currentSlide]];

    const nextSlide = () => setCurrentSlide(prev => (prev + 1) % positions.length);
    const prevSlide = () => setCurrentSlide(prev => (prev - 1 + positions.length) % positions.length);

    return (
        <div className="relative">
             <AnimatePresence>
                {isAdVisible && (
                    <AdBannerModal
                        desktopBannerUrl={electionConfig.desktopBannerUrl}
                        mobileBannerUrl={electionConfig.mobileBannerUrl}
                        onClose={handleAdClose}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {viewState === 'branding' && (
                    <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1, rotate: 360 }} exit={{ opacity: 0, scale: 0 }} transition={{ duration: 1, ease: "easeInOut" }} className="text-center p-10">
                        <h2 className="text-4xl font-bold text-slate-800">This software developed by <a href="https://finixia.in" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Finixia Dedecons</a></h2>
                        <h1 className="text-6xl font-extrabold text-slate-900 mt-4">
                            <a href="https://finixia.in/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 transition-colors">
                                Finixia dedecons
                            </a>
                        </h1>
                        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://finixia.in/about-us" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-700 hover:text-slate-900 hover:underline transition-colors" title="About Finixia dedecons">
                                About Finixia dedecons
                            </a>
                            <a href="https://finixia.in/contact-us" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-700 hover:text-slate-900 hover:underline transition-colors" title="Connect with Finixia dedecons">
                                Connect with Finixia dedecons
                            </a>
                        </div>
                    </motion.div>
                )}
                {viewState === 'countdown' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center p-10">
                        <h2 className="text-3xl font-semibold text-slate-700 mb-4">Results will appear in...</h2>
                        <p className="text-9xl font-bold text-slate-900">{countdown}</p>
                    </motion.div>
                )}
                {viewState === 'results' && !loading && positions.length > 0 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <h2 className="text-xl font-bold mb-4">Election Results</h2>
                        <AnimatePresence mode="wait">
                            <motion.div 
                                key={currentSlide}
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Card>
                                    <h3 className="text-lg font-bold capitalize mb-4">{positions[currentSlide]}</h3>
                                    <ul className="space-y-4">
                                        {currentPositionData.map((c, index) => {
                                            const maxVotes = Math.max(...currentPositionData.map(c => c.votes), 0);
                                            return (
                                                <li key={c._id} className="flex items-center gap-4">
                                                    <div className="relative">
                                                        <img src={c.photo || `https://placehold.co/40x40/E0E0E0/333?text=${c.name.charAt(0)}`} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                                                        {index === 0 && c.votes > 0 && (
                                                            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full shadow-lg">WINNER</motion.div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex justify-between mb-1">
                                                            <span className="font-semibold">{c.name}</span>
                                                            <span className="font-bold">{c.votes} Votes</span>
                                                        </div>
                                                        <AnimatedBar value={c.votes} maxValue={maxVotes} />
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </Card>
                            </motion.div>
                        </AnimatePresence>
                         <div className="flex justify-between mt-4">
                            <Button onClick={prevSlide} variant="secondary"><ArrowLeft size={16}/> Previous</Button>
                            <Button onClick={nextSlide} variant="secondary">Next <ArrowRight size={16}/></Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            {loading && viewState === 'results' && <p>Loading results...</p>}
        </div>
    );
}

function AppSettings({ config, refetchConfig }) {
    const [newAppTitle, setNewAppTitle] = useState(config.appTitle || 'Finelectra');
    const [newLogoUrl, setNewLogoUrl] = useState(config.logoUrl || 'https://finixia.in/logo%27s/logo-dark.png');
    const [newSecondaryLogoUrl, setNewSecondaryLogoUrl] = useState(config.secondaryLogoUrl || '');
    const [newQrIconUrl, setNewQrIconUrl] = useState(config.qrIconUrl || '');
    const [newCollegeName, setNewCollegeName] = useState(config.collegeName || '');
    const [newCollegeLogoUrl, setNewCollegeLogoUrl] = useState(config.collegeLogoUrl || '');
    const { data: positions, loading, refetch: refetchPositions } = useApi('/admin/positions');
    const [newPosition, setNewPosition] = useState('');
    const [newDesktopBannerUrl, setNewDesktopBannerUrl] = useState(config.desktopBannerUrl || '');
    const [newMobileBannerUrl, setNewMobileBannerUrl] = useState(config.mobileBannerUrl || '');
    const [newLeftSignHolderTitle, setNewLeftSignHolderTitle] = useState(config.leftSignHolderTitle || '');
    const [newRightSignHolderTitle, setNewRightSignHolderTitle] = useState(config.rightSignHolderTitle || '');
    const [newPdfProviderLogoUrl, setNewPdfProviderLogoUrl] = useState(config.pdfProviderLogoUrl || '');
    const [newPdfProviderText, setNewPdfProviderText] = useState(config.pdfProviderText || '');
    // --- NEW: Added state for electionDate ---
    const [newElectionDate, setNewElectionDate] = useState(config.electionDate || '');
    const [newStaffPin, setNewStaffPin] = useState(config.staffPin || '');


    const handleUpdateSetting = async (key, value) => {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/admin/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, body: JSON.stringify({ key, value }) });
        refetchConfig();
    };
    
    const handleAddPosition = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/admin/positions`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, body: JSON.stringify({ name: newPosition }) });
        setNewPosition('');
        refetchPositions();
        refetchConfig();
    };

    const handleDeletePosition = async (id) => {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/admin/positions/${id}`, { method: 'DELETE', headers: { 'x-auth-token': token } });
        refetchPositions();
        refetchConfig();
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Application Settings</h2>
            <Card className="mb-6">
                <h3 className="text-lg font-semibold mb-3">General Branding</h3>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <Input id="appTitle" placeholder="Application Title" value={newAppTitle} onChange={e => setNewAppTitle(e.target.value)} icon={<FileText size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('appTitle', newAppTitle)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="logoUrl" placeholder="Primary Logo URL" value={newLogoUrl} onChange={e => setNewLogoUrl(e.target.value)} icon={<ImageIcon size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('logoUrl', newLogoUrl)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="secondaryLogoUrl" placeholder="Secondary Logo URL" value={newSecondaryLogoUrl} onChange={e => setNewSecondaryLogoUrl(e.target.value)} icon={<ImageIcon size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('secondaryLogoUrl', newSecondaryLogoUrl)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="qrIconUrl" placeholder="QR Scanner Icon URL (512x512)" value={newQrIconUrl} onChange={e => setNewQrIconUrl(e.target.value)} icon={<QrCode size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('qrIconUrl', newQrIconUrl)}>Save</Button>
                    </div>
                </div>
            </Card>

            <Card className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Advertisement Settings</h3>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <Input id="desktopBannerUrl" placeholder="Desktop Banner URL (e.g., 800x400px)" value={newDesktopBannerUrl} onChange={e => setNewDesktopBannerUrl(e.target.value)} icon={<ImageIcon size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('desktopBannerUrl', newDesktopBannerUrl)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="mobileBannerUrl" placeholder="Mobile Banner URL (e.g., 320x480px)" value={newMobileBannerUrl} onChange={e => setNewMobileBannerUrl(e.target.value)} icon={<ImageIcon size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('mobileBannerUrl', newMobileBannerUrl)}>Save</Button>
                    </div>
                </div>
            </Card>

            <Card className="mb-6">
                <h3 className="text-lg font-semibold mb-3">PDF Header & Provider Settings</h3>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <Input id="collegeName" placeholder="College Name & Details (for PDF Header)" value={newCollegeName} onChange={e => setNewCollegeName(e.target.value)} icon={<Building2 size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('collegeName', newCollegeName)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="collegeLogoUrl" placeholder="College Logo URL (for PDF Header)" value={newCollegeLogoUrl} onChange={e => setNewCollegeLogoUrl(e.target.value)} icon={<ImageIcon size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('collegeLogoUrl', newCollegeLogoUrl)}>Save</Button>
                    </div>
                     {/* --- NEW: Input for electionDate --- */}
                    <div className="flex items-center gap-4">
                        <Input id="electionDate" placeholder="Date of Election (e.g., 25-09-2025)" value={newElectionDate} onChange={e => setNewElectionDate(e.target.value)} icon={<CalendarDays size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('electionDate', newElectionDate)}>Save</Button>
                    </div>
                    <hr className="my-2 border-slate-200" />
                    <div className="flex items-center gap-4">
                        <Input id="pdfProviderLogoUrl" placeholder="Provider Logo URL (for PDF Footer)" value={newPdfProviderLogoUrl} onChange={e => setNewPdfProviderLogoUrl(e.target.value)} icon={<ImageIcon size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('pdfProviderLogoUrl', newPdfProviderLogoUrl)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="pdfProviderText" placeholder="Provider Credit Text (for PDF Footer)" value={newPdfProviderText} onChange={e => setNewPdfProviderText(e.target.value)} icon={<FileText size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('pdfProviderText', newPdfProviderText)}>Save</Button>
                    </div>
                </div>
            </Card>

            <Card className="mb-6">
                <h3 className="text-lg font-semibold mb-3">PDF Signature Settings</h3>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <Input id="leftSignHolderTitle" placeholder="Left Signature Holder Title (e.g., Principal)" value={newLeftSignHolderTitle} onChange={e => setNewLeftSignHolderTitle(e.target.value)} icon={<Edit3 size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('leftSignHolderTitle', newLeftSignHolderTitle)}>Save</Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input id="rightSignHolderTitle" placeholder="Right Signature Holder Title (e.g., Returning Officer)" value={newRightSignHolderTitle} onChange={e => setNewRightSignHolderTitle(e.target.value)} icon={<Edit3 size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('rightSignHolderTitle', newRightSignHolderTitle)}>Save</Button>
                    </div>
                </div>
            </Card>
            
            <Card className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Controls</h3>
                <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <span className="font-semibold">Emergency Logout Button</span>
                        <Button 
                            onClick={() => handleUpdateSetting('emergencyLogoutEnabled', !config.emergencyLogoutEnabled)}
                            variant={config.emergencyLogoutEnabled ? 'danger' : 'success'}
                            className="w-40"
                        >
                            {config.emergencyLogoutEnabled ? <><ShieldOff size={16}/> Disable</> : <><ShieldCheck size={16}/> Enable</>}
                        </Button>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <span className="font-semibold">Fullscreen Button</span>
                         <Button 
                            onClick={() => handleUpdateSetting('fullscreenButtonEnabled', !config.fullscreenButtonEnabled)}
                            variant={config.fullscreenButtonEnabled ? 'danger' : 'success'}
                            className="w-40"
                        >
                            {config.fullscreenButtonEnabled ? <><ShieldOff size={16}/> Hide</> : <><ShieldCheck size={16}/> Show</>}
                        </Button>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <span className="font-semibold">Public Results Screen</span>
                         <Button 
                            onClick={() => handleUpdateSetting('publicScreenEnabled', !config.publicScreenEnabled)}
                            variant={config.publicScreenEnabled ? 'danger' : 'success'}
                            className="w-40"
                        >
                            {config.publicScreenEnabled ? <><Tv2 size={16}/> Disable</> : <><Tv2 size={16}/> Enable</>}
                        </Button>
                    </div>
                </div>
            </Card>

            <Card className="mb-6">
                <h3 className="text-lg font-semibold mb-3 border-b pb-2 text-red-600 flex items-center gap-2"><Lock size={20}/> Access Control</h3>
                <div className="space-y-4 pt-2">
                    <div className="flex items-center gap-4">
                        <Input id="staffPin" type="password" placeholder="Staff / Monitor Login PIN (e.g. 4-6 digits)" value={newStaffPin} onChange={e => setNewStaffPin(e.target.value)} icon={<Lock size={18}/>} />
                        <Button onClick={() => handleUpdateSetting('staffPin', newStaffPin)} variant="danger">Save PIN</Button>
                    </div>
                    <p className="text-xs text-slate-500">This PIN is required for accessing the `/login` portal. If left blank, anyone can access the login page.</p>
                </div>
            </Card>

            <Card>
                <h3 className="text-lg font-semibold mb-3">Candidate Positions</h3>
                <form onSubmit={handleAddPosition} className="flex items-center gap-4 mb-4">
                    <Input id="newPosition" placeholder="New Position Name" value={newPosition} onChange={e => setNewPosition(e.target.value)} icon={<Tag size={18}/>} />
                    <Button type="submit">Add</Button>
                </form>
                {loading ? <p>Loading...</p> : <ul className="space-y-2">{(positions || []).map(p => <li key={p._id} className="flex justify-between items-center p-2 bg-slate-50 rounded"><span>{p.name}</span><button onClick={() => handleDeletePosition(p._id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 size={16}/></button></li>)}</ul>}
            </Card>
        </div>
    );
}

function UserManual() {
    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Admin Panel: User Manual</h2>
            <Card className="prose prose-sm max-w-none">
                <h3>1. Initial Login</h3>
                <p>Select the <strong>"Admin"</strong> tab and enter the credentials from your <code>.env</code> file.</p>
                <h3>2. Step-by-Step Election Workflow</h3>
                <h4>Step 1: Configure Settings</h4>
                <p>Go to the <strong>"Settings"</strong> tab. Set your application title, logo URLs, and all PDF-related details like College Name, Election Date, and signature titles. Create all necessary candidate positions and configure monitor station controls.</p>
                <h4>Step 2: Add Voters and Candidates</h4>
                <p>Go to the <strong>"Voters"</strong> and <strong>"Candidates"</strong> tabs to add all participants. You can edit or delete any entry after it has been created using the icons next to each name.</p>
                <h4>Step 3: Generate QR Codes</h4>
                <p>Navigate to the <strong>"Election Control"</strong> tab. Enter the number of QR codes you need and click <strong>"Generate & Download PDF"</strong>. You can do this before or during an active election to add more voters.</p>
                <h4>Step 4: Run the Election</h4>
                <p>On the Election Control tab, click <strong>"Start Election"</strong>. Monitors can now log in at polling stations.</p>
                <h4>Step 5: Monitor the Election</h4>
                <p>Use the <strong>"Live Dashboard"</strong> to see votes in real-time and monitor station status. Use the <strong>"Monitor Tracking"</strong> page for detailed scan logs.</p>
                <h4>Step 6: Access Public Screen</h4>
                <p>Click the <strong>"Public Screen"</strong> button in the navigation menu to open the live results display in a new tab. This is meant for a large projector or TV screen for public viewing. It only works if enabled in Settings.</p>
                <h4>Step 7: Publish and View Results</h4>
                <p>After the election ends (by clicking <strong>"Stop Election"</strong>), a <strong>"Publish Results"</strong> button will appear. Click it, then go to the <strong>"Results"</strong> tab and click <strong>"Show Results"</strong>.</p>
                <h4>Step 8: Start a New Election</h4>
                <p>Once results are published, a <strong>"Start New Election"</strong> button appears. This will wipe all previous votes and QR codes and archive the results.</p>
                <h3>Polling Station Monitor Instructions</h3>
                <p>Log in using the "Voter / Monitor" tab. The screen will show the QR scanner. If visible, press the fullscreen button to prevent accidental clicks. The logout button is hidden during an active election unless enabled remotely by an admin for emergencies.</p>
            </Card>
        </div>
    );
}

function ArchivedResults() {
    const { data: archivedResults, loading, error } = useApi('/admin/archived-results');

    if (loading && !archivedResults) return <Card><p className="text-center">Fetching archived results...</p></Card>;
    if (error) return <Card><p className="text-red-500">Error: {error}</p></Card>;
    if (!archivedResults || archivedResults.length === 0) return <Card><p className="text-center">No archived results found.</p></Card>;

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Archived Election Results</h2>
            <div className="space-y-6">
                {(archivedResults || []).map(archive => {
                    const groupedResults = archive.results.reduce((acc, candidate) => {
                        (acc[candidate.position] = acc[candidate.position] || []).push(candidate);
                        return acc;
                    }, {});

                    return (
                        <Card key={archive._id}>
                            <h3 className="text-lg font-bold mb-3">Election Year: {archive.year}</h3>
                            {Object.keys(groupedResults).length > 0 ? (
                                <div className="space-y-4">
                                    {Object.entries(groupedResults).map(([positionName, candidates]) => (
                                        <div key={positionName} className="mb-4">
                                            <h4 className="font-semibold text-slate-700 capitalize">{positionName}</h4>
                                            <ul className="list-disc pl-5 space-y-1">
                                                {candidates.sort((a, b) => b.votes - a.votes).map(c => (
                                                    <li key={c._id || c.name}>{c.name}: {c.votes} votes</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No results recorded for this year.</p>
                            )}
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}

function ElectionControl({ electionConfig, refetchElectionStatus }) {
    const [qrCount, setQrCount] = useState(100);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const handleStatusChange = async (status, resultsPublished = electionConfig.election.resultsPublished) => {
        if (status === 'Finished') {
            if (!window.confirm("Are you sure you want to end the election? No more votes can be cast.")) {
                return;
            }
        }
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/admin/election/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, body: JSON.stringify({ status, resultsPublished }) });
        refetchElectionStatus();
    };

    const handleGeneratePdf = async () => {
        setIsDownloading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/admin/generate-qrcodes?count=${qrCount}&electionStatus=${electionConfig.election?.status}`, { headers: { 'x-auth-token': token } });
            if (!res.ok) throw new Error('PDF Generation failed');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `qrcodes_${qrCount}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) { console.error("Download error:", error); alert("Failed to download PDF."); } finally { setIsDownloading(false); }
    };
    
    const handleResetElection = async () => {
        if (window.confirm("Are you sure? This will delete all current votes, QR codes, and scan logs, and will archive the current results.")) {
            setIsResetting(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/admin/election/reset`, { 
                    method: 'POST', 
                    headers: { 'Content-Type': 'application/json', 'x-auth-token': token } 
                });
                if (!res.ok) {
                    const errorData = await res.json().catch(() => ({ msg: 'An unknown error occurred on the server.' }));
                    throw new Error(errorData.msg || 'Failed to reset election');
                }
                await refetchElectionStatus();
            } catch (error) { 
                console.error("Reset error:", error); 
                alert(`Failed to start new election: ${error.message}`); 
            } finally { 
                setIsResetting(false); 
            }
        }
    };

    const handleDownloadPublishedResults = async () => {
        setIsDownloading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/admin/results/published-download`, { headers: { 'x-auth-token': token } });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({ msg: 'An unknown error occurred during PDF generation.' }));
                throw new Error(errorData.msg || 'Failed to download published results PDF');
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `official_election_results_${new Date().getFullYear()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download published results error:", error);
            alert(`Failed to download published results PDF: ${error.message}`);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Election Control</h2>
            <Card className="mb-6">
                <p className="text-center mb-4">Current Status: <span className="font-bold text-lg">{electionConfig.election?.status}</span></p>
                <div className="space-y-2">
                    {electionConfig.election?.status === 'Pending' && <Button onClick={() => handleStatusChange('Active')} variant="success" className="w-full"><Play size={16}/> Start Election</Button>}
                    {electionConfig.election?.status === 'Active' && <Button onClick={() => handleStatusChange('Finished')} variant="danger" className="w-full"><StopCircle size={16}/> Stop Election</Button>}
                    {electionConfig.election?.status === 'Finished' && !electionConfig.election.resultsPublished && <Button onClick={() => handleStatusChange('Finished', true)} variant="primary" className="w-full"><Award size={16}/> Publish Results</Button>}
                    {electionConfig.election?.resultsPublished && (
                        <div className="space-y-4">
                            <p className="text-center text-green-600 font-semibold">Results are now public.</p>
                            <Button onClick={handleDownloadPublishedResults} variant="secondary" className="w-full" disabled={isDownloading}>{isDownloading ? 'Downloading...' : <><Download size={16}/> Download Published Results PDF</>}</Button>
                            <Button onClick={handleResetElection} variant="danger" className="w-full" disabled={isResetting}>{isResetting ? 'Resetting...' : <><RefreshCw size={16}/> Start New Election</>}</Button>
                        </div>
                    )}
                </div>
            </Card>
            <h2 className="text-xl font-bold my-4">QR Code Management</h2>
            <Card>
                <p className="text-slate-600 mb-4">
                    Generate unique QR codes for voters. 
                    <span className="font-semibold block text-sm mt-1">You can generate additional codes even after the election has started.</span>
                </p>
                <div className="flex items-center gap-4 mb-4">
                    <Input id="qrCount" type="number" placeholder="e.g., 100" value={qrCount} onChange={e => setQrCount(e.target.value)} icon={<QrCode size={18}/>} />
                </div>
                <Button onClick={handleGeneratePdf} variant="secondary" className="w-full" disabled={isDownloading}>{isDownloading ? 'Generating...' : <><UserPlus size={16}/> Generate & Download PDF</>}</Button>
            </Card>
        </div>
    );
}


function AdsManagerScreen() {
    const [ads, setAds] = useState([]);
    const [newAd, setNewAd] = useState({ imageUrl: '', targetUrl: '', type: 'banner' });
    const token = localStorage.getItem('token');
    
    useEffect(() => {
        fetch('/api/admin/ads', { headers: { 'x-auth-token': token }})
            .then(res => res.json())
            .then(data => setAds(Array.isArray(data) ? data : []))
            .catch(console.error);
    }, [token]);

    const handleAdd = async () => {
        if (!newAd.imageUrl) return;
        const res = await fetch('/api/admin/ads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
            body: JSON.stringify(newAd)
        });
        if (res.ok) {
            const added = await res.json();
            setAds([added, ...ads]);
            setNewAd({ imageUrl: '', targetUrl: '', type: 'banner' });
        }
    };
    
    const handleDelete = async (id) => {
        const res = await fetch(`/api/admin/ads/${id}`, {
            method: 'DELETE',
            headers: { 'x-auth-token': token }
        });
        if (res.ok) setAds(ads.filter(a => a._id !== id));
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Advertisement Manager</h2>
            <Card className="p-6">
                <h3 className="text-lg font-bold mb-4">Add New Advertisement</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <input type="text" placeholder="Image URL (Required)" className="w-full p-2 border border-slate-200 rounded-lg col-span-2" value={newAd.imageUrl} onChange={e => setNewAd({...newAd, imageUrl: e.target.value})} />
                    <input type="text" placeholder="Target Link URL (Optional)" className="w-full p-2 border border-slate-200 rounded-lg" value={newAd.targetUrl} onChange={e => setNewAd({...newAd, targetUrl: e.target.value})} />
                    <select className="w-full p-2 border border-slate-200 rounded-lg" value={newAd.type} onChange={e => setNewAd({...newAd, type: e.target.value})}>
                        <option value="banner">Carousel Banner</option>
                        <option value="featured">Featured Ad (Popup/Sidebar)</option>
                    </select>
                </div>
                <Button onClick={handleAdd}>Add Advertisement</Button>
            </Card>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                    {ads.map(ad => (
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} key={ad._id}>
                            <Card className="overflow-hidden flex flex-col p-0 h-full">
                                <img src={ad.imageUrl} alt="Ad" className="w-full h-32 object-cover bg-slate-100" />
                                <div className="p-4 flex-1">
                                    <span className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-wide ${ad.type === 'featured' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{ad.type}</span>
                                    <p className="mt-2 text-sm text-slate-500 truncate" title={ad.targetUrl}>Link: {ad.targetUrl || 'None'}</p>
                                </div>
                                <div className="p-4 border-t border-slate-100 bg-slate-50">
                                    <button className="text-red-500 text-sm font-semibold hover:underline" onClick={() => handleDelete(ad._id)}>Delete Ad</button>
                                </div>
                            </Card>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
}


// --- Nomination Application Form ---
function NominationForm({ electionConfig }) {
    const [formData, setFormData] = useState({
        fullName: '', rollNo: '', position: '', emailId: '', mobileNumber: '',
        course: '', yearOfStudy: '', residentialAddress: '', termsAccepted: false
    });
    const [files, setFiles] = useState({
        nationalityDoc: '', collegeIdDoc: '', passportPhoto: '', signature: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [error, setError] = useState('');

    
    const [compressing, setCompressing] = useState({});

    const handleCompressAndConvertToBase64 = async (file) => {
        if (!file) return '';
        try {
            const options = { maxSizeMB: 0.2, maxWidthOrHeight: 1024, useWebWorker: true };
            const compressedFile = await imageCompression(file, options);
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(compressedFile);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        } catch (error) {
            console.error('Compression error:', error);
            throw new Error('Failed to compress image.');
        }
    };

    const handleFileChange = async (e, field) => {
        const file = e.target.files[0];
        if (file) {
            // 1. Instant preview in milliseconds!
            const previewReader = new FileReader();
            previewReader.readAsDataURL(file);
            previewReader.onload = () => {
                setFiles(prev => ({ ...prev, [field]: previewReader.result }));
            };
            
            // 2. Compress in background
            setCompressing(prev => ({ ...prev, [field]: true }));
            try {
                const base64 = await handleCompressAndConvertToBase64(file);
                setFiles(prev => ({ ...prev, [field]: base64 })); // Update with compressed version
            } catch (err) {
                setError(err.message);
            } finally {
                setCompressing(prev => ({ ...prev, [field]: false }));
            }
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!formData.fullName || !formData.rollNo || !formData.position || !formData.emailId || !formData.mobileNumber || !formData.course || !formData.yearOfStudy || !formData.residentialAddress || !files.nationalityDoc || !files.passportPhoto || !files.signature || !formData.termsAccepted) {
            return setError('Please fill all required fields and upload mandatory documents.');
        }

        setIsSubmitting(true);
        try {
            const payload = { ...formData, ...files };
            const res = await fetch('/api/nominations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                setSubmitSuccess(true);
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to submit application.');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }
        setIsSubmitting(false);
    };

    if (submitSuccess) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center p-8">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle size={40} className="text-green-600" />
                    </motion.div>
                    <h2 className="text-3xl font-bold text-slate-800 mb-4">Application Submitted!</h2>
                    <p className="text-slate-600 mb-8">Your nomination form has been successfully submitted and is pending review by the election committee.</p>
                    <Button onClick={() => navigate('/')} className="w-full">Return Home</Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <div className="bg-slate-900 text-white py-20 relative overflow-hidden flex-shrink-0">
                <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
                    <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Candidate Nomination</h1>
                    <p className="text-lg text-slate-300">Apply to become an official candidate for {electionConfig?.appTitle || 'the election'}.</p>
                </div>
            </div>
            
            <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-12">
                <Card className="p-8 shadow-2xl rounded-3xl">
                    <h2 className="text-2xl font-bold mb-6 text-slate-800 border-b pb-4">Application Details</h2>
                    {error && <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2"><AlertTriangle size={20}/> {error}</div>}
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold mb-2">Full Name *</label>
                                <input type="text" className="w-full p-3 border rounded-lg" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Roll No *</label>
                                <input type="text" className="w-full p-3 border rounded-lg" value={formData.rollNo} onChange={e => setFormData({...formData, rollNo: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Position Applied For *</label>
                                <select className="w-full p-3 border rounded-lg bg-white" value={formData.position} onChange={e => setFormData({...formData, position: e.target.value})}>
                                    <option value="">Select Position...</option>
                                    {(electionConfig?.positions || []).map(p => (
                                        <option key={p.name} value={p.name}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Email ID *</label>
                                <input type="email" className="w-full p-3 border rounded-lg" value={formData.emailId} onChange={e => setFormData({...formData, emailId: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Mobile Number *</label>
                                <input type="tel" className="w-full p-3 border rounded-lg" value={formData.mobileNumber} onChange={e => setFormData({...formData, mobileNumber: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Course *</label>
                                <input type="text" className="w-full p-3 border rounded-lg" value={formData.course} onChange={e => setFormData({...formData, course: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Year of Study *</label>
                                <input type="text" className="w-full p-3 border rounded-lg" value={formData.yearOfStudy} onChange={e => setFormData({...formData, yearOfStudy: e.target.value})} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold mb-2">Residential Address *</label>
                                <textarea className="w-full p-3 border rounded-lg" rows="3" value={formData.residentialAddress} onChange={e => setFormData({...formData, residentialAddress: e.target.value})} />
                            </div>
                        </div>

                        <h2 className="text-2xl font-bold mt-10 mb-6 text-slate-800 border-b pb-4">Document Uploads</h2>
                        <p className="text-sm text-slate-500 mb-6">Images will be automatically compressed before upload.</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { key: 'nationalityDoc', label: 'Doc of Nationality *' },
                                { key: 'collegeIdDoc', label: 'College ID Card (Optional)' },
                                { key: 'passportPhoto', label: 'Passport Photo *' },
                                { key: 'signature', label: 'Signature *' }
                            ].map(doc => (
                                <Card key={doc.key} className="bg-slate-50 border-dashed border-2 relative overflow-hidden">
                                    <label className="block text-sm font-semibold mb-2">{doc.label}</label>
                                    <input type="file" accept="image/*" onChange={e => handleFileChange(e, doc.key)} className="w-full text-sm mb-2" />
                                    {files[doc.key] && (
                                        <div className="mt-2 relative">
                                            <img src={files[doc.key]} alt="Preview" className="w-full h-32 object-cover rounded-lg shadow-sm" />
                                            {compressing[doc.key] ? (
                                                <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg">
                                                    <span className="text-xs font-bold text-indigo-600 animate-pulse">Processing...</span>
                                                </div>
                                            ) : (
                                                <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-md font-bold flex items-center gap-1 shadow-md">
                                                    <Check size={12}/> Ready
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>

                        <div className="mt-8 bg-blue-50 p-6 rounded-xl border border-blue-100 flex items-start gap-4">
                            <input type="checkbox" id="terms" className="mt-1 w-5 h-5 cursor-pointer" checked={formData.termsAccepted} onChange={e => setFormData({...formData, termsAccepted: e.target.checked})} />
                            <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                                By submitting this form, I declare that all information provided is true and accurate. I understand that my IP address will be recorded for security purposes. I agree to abide by the election rules and regulations.
                            </label>
                        </div>

                        <Button type="submit" disabled={isSubmitting} className="w-full py-4 text-lg mt-8">
                            {isSubmitting ? 'Submitting Application...' : 'Submit Nomination'}
                        </Button>
                    </form>
                </Card>
            </div>
            
            <footer className="text-center mt-12 pb-6 text-slate-500 text-sm flex flex-col gap-1">
                <span>&copy; {new Date().getFullYear()} {electionConfig?.appTitle || 'Finelectra'}. All rights reserved.</span>
                <span className="text-xs text-slate-600 font-semibold mt-1 tracking-wider uppercase">Platform developed by <a href="https://finixia.in" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600 transition-colors">Finixia Dedecons</a></span>
            </footer>
        </div>
    );
}

// --- Admin Nominations Dashboard ---
function AdminNominationsScreen() {
    const [nominations, setNominations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloadingZip, setDownloadingZip] = useState(false);
    const [inspectNomination, setInspectNomination] = useState(null);
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchNominations();
    }, []);

    const fetchNominations = async () => {
        try {
            const res = await fetch('/api/admin/nominations', { headers: { 'x-auth-token': token } });
            if (res.ok) setNominations(await res.json());
            else setError('Failed to load nominations');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, action) => {
        try {
            const res = await fetch(`/api/admin/nominations/${id}/${action}`, {
                method: 'PUT', headers: { 'x-auth-token': token }
            });
            if (res.ok) {
                fetchNominations();
            } else {
                const data = await res.json();
                alert(data.error || 'Update failed');
            }
        } catch (err) {
            alert('Network error');
        }
    };

    
    const handleDownloadZip = async () => {
        setDownloadingZip(true);
        try {
            const zip = new JSZip();
            nominations.forEach((nom) => {
                const folder = zip.folder(`${nom.fullName.replace(/[^a-z0-9]/gi, '_')}_${nom.rollNo}`);
                
                // 1. Generate PDF for the application form
                const doc = new jsPDF();
                doc.setFontSize(22);
                doc.text('Candidate Application Form', 20, 20);
                doc.setFontSize(12);
                
                const lines = [
                    `Full Name: ${nom.fullName}`,
                    `Roll No: ${nom.rollNo}`,
                    `Position Applied: ${nom.position}`,
                    `Email: ${nom.emailId}`,
                    `Mobile: ${nom.mobileNumber}`,
                    `Course: ${nom.course}`,
                    `Year of Study: ${nom.yearOfStudy}`,
                    `Residential Address: ${nom.residentialAddress}`,
                    `Status: ${nom.status}`,
                    `IP Address: ${nom.ipAddress}`,
                    `Date Submitted: ${new Date(nom.createdAt).toLocaleString()}`
                ];
                
                let y = 40;
                lines.forEach(line => {
                    doc.text(line, 20, y);
                    y += 10;
                });
                
                const pdfBlob = doc.output('blob');
                folder.file('Application_Form.pdf', pdfBlob);

                // 2. Add raw images
                const addBase64File = (base64Str, filename) => {
                    if (!base64Str) return;
                    const parts = base64Str.split(';base64,');
                    if (parts.length === 2) {
                        const ext = parts[0].split('/')[1] || 'png';
                        folder.file(`${filename}.${ext}`, parts[1], {base64: true});
                    }
                };

                addBase64File(nom.nationalityDoc, 'Nationality_Doc');
                addBase64File(nom.collegeIdDoc, 'College_ID');
                addBase64File(nom.passportPhoto, 'Passport_Photo');
                addBase64File(nom.signature, 'Signature');
            });
            
            const content = await zip.generateAsync({type: 'blob'});
            saveAs(content, 'Candidate_Nominations.zip');
        } catch (err) {
            alert('Failed to generate ZIP');
            console.error(err);
        }
        setDownloadingZip(false);
    };


    if (loading) return <div>Loading nominations...</div>;
    if (error) return <div className="text-red-500">{error}</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">Nomination Screening</h2>
                <Button onClick={handleDownloadZip} disabled={downloadingZip} variant="secondary">
                    {downloadingZip ? 'Generating ZIP...' : <><Download size={16}/> Download All Forms (ZIP)</>}
                </Button>
            </div>
            
            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-slate-800 uppercase font-semibold">
                        <tr>
                            <th className="p-4">Applicant</th>
                            <th className="p-4">Position</th>
                            <th className="p-4">Contact</th>
                            <th className="p-4">Status</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {nominations.map(nom => (
                            <tr key={nom._id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="p-4">
                                    <div className="font-bold text-slate-900">{nom.fullName}</div>
                                    <div className="text-xs">Roll: {nom.rollNo}</div>
                                    <div className="text-xs">{nom.course} ({nom.yearOfStudy})</div>
                                </td>
                                <td className="p-4 font-semibold text-indigo-600">{nom.position}</td>
                                <td className="p-4">
                                    <div>{nom.mobileNumber}</div>
                                    <div className="text-xs">{nom.emailId}</div>
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${nom.status === 'Accepted' ? 'bg-green-100 text-green-700' : nom.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {nom.status}
                                    </span>
                                </td>
                                <td className="p-4 text-right space-x-2">
                                    {nom.status === 'Pending' && (
                                        <>
                                            <button onClick={() => updateStatus(nom._id, 'accept')} className="p-2 bg-green-500 text-white rounded hover:bg-green-600 shadow" title="Accept & Convert to Candidate"><Check size={16}/></button>
                                            <button onClick={() => updateStatus(nom._id, 'reject')} className="p-2 bg-red-500 text-white rounded hover:bg-red-600 shadow" title="Reject"><X size={16}/></button>
                                        </>
                                    )}
                                    <button onClick={() => setInspectNomination(nom)} className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 shadow ml-2" title="Inspect Documents"><Search size={16}/></button>
                                </td>
                            </tr>
                        ))}
                        {nominations.length === 0 && (
                            <tr><td colSpan="5" className="p-8 text-center text-slate-500">No nominations received yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {inspectNomination && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <Card className="max-w-4xl w-full my-8 relative">
                        <button onClick={() => setInspectNomination(null)} className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full hover:bg-slate-200"><X size={20}/></button>
                        <h2 className="text-2xl font-bold mb-6">Inspect Nomination Documents</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <p className="text-sm font-bold text-slate-500">Applicant Name</p>
                                <p className="text-lg font-semibold">{inspectNomination.fullName}</p>
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-500">Applied Position</p>
                                <p className="text-lg font-semibold text-indigo-600">{inspectNomination.position}</p>
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-500">Roll No / Course</p>
                                <p className="text-lg font-semibold">{inspectNomination.rollNo} / {inspectNomination.course} ({inspectNomination.yearOfStudy})</p>
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-500">Contact</p>
                                <p className="text-lg font-semibold">{inspectNomination.mobileNumber} / {inspectNomination.emailId}</p>
                            </div>
                        </div>
                        
                        <h3 className="text-xl font-bold mb-4 border-b pb-2">Submitted Documents</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-sm font-bold mb-2">Doc of Nationality</p>
                                <img src={inspectNomination.nationalityDoc} alt="Nationality" className="w-full rounded border cursor-pointer hover:opacity-90 max-h-64 object-contain bg-slate-50" onClick={() => window.open(inspectNomination.nationalityDoc, '_blank')} />
                            </div>
                            <div>
                                <p className="text-sm font-bold mb-2">Passport Photo</p>
                                <img src={inspectNomination.passportPhoto} alt="Photo" className="w-full rounded border cursor-pointer hover:opacity-90 max-h-64 object-contain bg-slate-50" onClick={() => window.open(inspectNomination.passportPhoto, '_blank')} />
                            </div>
                            <div>
                                <p className="text-sm font-bold mb-2">Signature</p>
                                <img src={inspectNomination.signature} alt="Signature" className="w-full rounded border cursor-pointer hover:opacity-90 max-h-64 object-contain bg-slate-50" onClick={() => window.open(inspectNomination.signature, '_blank')} />
                            </div>
                            {inspectNomination.collegeIdDoc && (
                                <div>
                                    <p className="text-sm font-bold mb-2">College ID (Optional)</p>
                                    <img src={inspectNomination.collegeIdDoc} alt="College ID" className="w-full rounded border cursor-pointer hover:opacity-90 max-h-64 object-contain bg-slate-50" onClick={() => window.open(inspectNomination.collegeIdDoc, '_blank')} />
                                </div>
                            )}
                        </div>
                        <div className="mt-8 pt-6 border-t flex justify-end">
                            <button onClick={() => setInspectNomination(null)} className="px-8 py-3 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold shadow transition-colors">Close Preview</button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

function AdminDashboard({ user, onLogout, electionConfig, refetchElectionStatus }) {
    const [page, setPage] = useState('live-dashboard');
    const [showMenu, setShowMenu] = useState(false);

    const renderAdminPage = () => {
        switch(page) {
            case 'live-dashboard': return <LiveDashboard />;
            case 'monitor-tracking': return <MonitorTracking />;
            case 'election-control': return <ElectionControl electionConfig={electionConfig} refetchElectionStatus={refetchElectionStatus} />;
            case 'voters': return <ManageVoters />;
            case 'candidates': return <ManageCandidates positions={electionConfig.positions || []} />;
            case 'results': return <ViewResults electionStatus={electionConfig.election} electionConfig={electionConfig} />;
            case 'settings': return <AppSettings config={electionConfig} refetchConfig={refetchElectionStatus} />;
            case 'manual': return <UserManual />;
            case 'ads': return <AdsManagerScreen />;
            case 'nominations': return <AdminNominationsScreen />;
            case 'archive': return <ArchivedResults />;
            case 'audit-logs': return <AuditLogDashboard />;
            default: return <LiveDashboard />;
        }
    };

    const NavButton = ({ pageName, icon, children, onClick }) => (
         <button onClick={() => { if(onClick) { onClick(); } else { setPage(pageName); } setShowMenu(false); }} className={`w-full text-left p-3 rounded-lg hover:bg-slate-100 font-semibold flex items-center gap-3 transition-colors ${page === pageName ? 'bg-slate-200' : ''}`}>
            {icon} {children}
        </button>
    );

    return (
        <div className="w-full min-h-screen px-4 sm:px-6 lg:px-8">
            <header className="flex justify-between items-center py-4 border-b border-slate-200">
                <div className="flex items-center gap-4">
                    <button className="md:hidden p-2 text-slate-600 hover:text-slate-900" onClick={() => setShowMenu(!showMenu)}><Menu size={28} /></button>
                    <h1 className="text-2xl md:text-3xl font-bold">{electionConfig.appTitle ? `${electionConfig.appTitle} Admin` : 'Finelectra Admin Panel'}</h1>
                </div>
                <Button onClick={onLogout} variant="secondary"><LogOut size={16}/> Logout</Button>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                <aside className={`fixed inset-y-0 left-0 bg-white z-40 w-64 p-6 shadow-xl transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:col-span-1 md:bg-transparent md:p-0 md:shadow-none ${showMenu ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="h-full md:max-h-[calc(100vh-8rem)] md:overflow-y-auto md:pr-2">
                        <Card className="md:p-0 md:bg-transparent md:shadow-none">
                            <div className="flex justify-between items-center mb-6 md:hidden"><h2 className="text-xl font-bold">Menu</h2><button onClick={() => setShowMenu(false)} className="p-1 rounded-full hover:bg-slate-200"><XCircle size={24} /></button></div>
                            <nav className="space-y-2">
                                <NavButton pageName="live-dashboard" icon={<Activity size={18} />}>Live Dashboard</NavButton>
                                <NavButton pageName="monitor-tracking" icon={<UserCheck size={18} />}>Monitor Tracking</NavButton>
                                <NavButton pageName="election-control" icon={<BarChart2 size={18} />}>Election Control</NavButton>
                                <NavButton pageName="voters" icon={<Users size={18} />}>Voters</NavButton>
                                <NavButton pageName="candidates" icon={<Award size={18} />}>Candidates</NavButton>
                                <NavButton pageName="results" icon={<BarChart2 size={18} />}>Current Results</NavButton>
                                <NavButton pageName="archive" icon={<Archive size={18} />}>Archived Results</NavButton>
                                <NavButton pageName="audit-logs" icon={<ShieldCheck size={18} />}>Audit Logs</NavButton>
                                <NavButton pageName="settings" icon={<Settings size={18} />}>Settings</NavButton>
                                <NavButton pageName="ads" icon={<Tv2 size={18} />}>Ads Manager</NavButton>
                                <NavButton pageName="nominations" icon={<FileText size={18} />}>Nominations</NavButton>
                                <NavButton pageName="manual" icon={<BookOpen size={18} />}>User Manual</NavButton>
                                <hr/>
                                <NavButton pageName="public-screen" icon={<Tv2 size={18}/>} onClick={() => window.open('/#/public', '_blank')}>Public Screen</NavButton>
                            </nav>
                        </Card>
                    </div>
                </aside>
                {showMenu && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" onClick={() => setShowMenu(false)}></div>}
                <main className="md:col-span-3">{renderAdminPage()}</main>
            </div>
            <footer className="text-center text-slate-500 text-sm mt-8 py-4">
                <div className="mb-2">
                    <span className="spin-text-animation">
                        {electionConfig.appTitle || 'Finelectra'} is developed by <a href="https://finixia.in/" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">Finixia dedecons</a>
                    </span>
                </div>
                <a href="https://finixia.in/about-us" title="About Finixia dedecons" target="_blank" rel="noopener noreferrer" className="hover:underline">About Us</a>
                <span className="mx-2 text-slate-400">|</span>
                <a href="https://finixia.in/contact-us" title="Connect with Finixia dedecons" target="_blank" rel="noopener noreferrer" className="hover:underline">Contact Us</a>
            </footer>
        </div>
    );
}

function MonitorScreen({ onLogout, isScriptLoaded, electionConfig, refetchConfig }) {
    const [page, setPage] = useState('scan');
    const [qrCodeId, setQrCodeId] = useState(null);
    const [error, setError] = useState('');
    const [scanStatus, setScanStatus] = useState('idle');
    const audioCtx = useRef(null);

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };
    
    useEffect(() => {
        const sendHeartbeat = async () => {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    await fetch(`${API_URL}/api/vote/heartbeat`, {
                        method: 'POST',
                        headers: { 'x-auth-token': token }
                    });
                }
            } catch (err) {
                console.error("Heartbeat failed:", err);
            }
        };
        sendHeartbeat();
        const intervalId = setInterval(sendHeartbeat, 45000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const initializeAudio = () => {
            if (!audioCtx.current) {
                audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
            }
        };
        document.body.addEventListener('click', initializeAudio, { once: true });
        return () => document.body.removeEventListener('click', initializeAudio);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => { refetchConfig(); }, 10000); 
        return () => clearInterval(interval);
    }, [refetchConfig]);
    
    const playSound = (isSuccess) => {
        if (!audioCtx.current || audioCtx.current.state === 'suspended') {
            audioCtx.current?.resume();
        }
        if (!audioCtx.current) return;

        const oscillator = audioCtx.current.createOscillator();
        const gainNode = audioCtx.current.createGain();
        oscillator.type = isSuccess ? 'sine' : 'square';
        oscillator.frequency.setValueAtTime(isSuccess ? 880 : 330, audioCtx.current.currentTime);
        if (!isSuccess) {
            oscillator.frequency.exponentialRampToValueAtTime(165, audioCtx.current.currentTime + 0.2);
        }
        gainNode.gain.setValueAtTime(0.25, audioCtx.current.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.current.currentTime + 0.3);
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.current.destination);
        oscillator.start();
        oscillator.stop(audioCtx.current.currentTime + 0.3);
    };

    const handleScanSuccess = (qrId) => {
        setQrCodeId(qrId);
        setPage('vote');
        setScanStatus('success');
        playSound(true);
        setTimeout(() => setScanStatus('idle'), 1500);
    };
    
    const handleScanError = (msg) => {
        setError(msg);
        setScanStatus('error');
        playSound(false);
        setTimeout(() => {
            setError('');
            setPage('scan');
            setScanStatus('idle');
        }, 3000);
    };

    const handleVoteFinish = () => {
        setPage('thanks');
    };

    const handleReturnToScanner = () => {
        setTimeout(() => {
            setPage('scan');
            setQrCodeId(null);
            setError('');
        }, 3000);
    };

    const renderContent = () => {
        switch (page) {
            case 'vote': return <VotingForm onVoteSubmit={handleVoteFinish} qrCodeId={qrCodeId} electionConfig={electionConfig} />;
            case 'thanks': return <ThankYouScreen onFinish={handleReturnToScanner} />;
            case 'scan': default:
                return <QRScannerScreen onScanSuccess={handleScanSuccess} isScriptLoaded={isScriptLoaded} onError={handleScanError} scanStatus={scanStatus} qrIconUrl={electionConfig.qrIconUrl} />;
        }
    };

    const showLogoutButton = electionConfig.election.status !== 'Active' || electionConfig.emergencyLogoutEnabled;

    return (
        <div className="max-w-md mx-auto relative">
             {electionConfig.fullscreenButtonEnabled && (
                <div className="absolute -top-2 right-0 z-10">
                    <Button onClick={toggleFullScreen} variant="secondary" className="w-auto p-3 shadow-lg">
                        <Maximize size={18}/>
                    </Button>
                </div>
            )}
            
            {showLogoutButton && (
                 <div className="absolute -top-2 left-0 z-10">
                    <Button onClick={onLogout} variant="secondary" className="w-auto shadow-lg">
                        <LogOut size={16}/> Logout Monitor
                    </Button>
                </div>
            )}
            
            <div className="pt-12">
                {error && <div className="p-4 mb-4 bg-red-100 text-red-800 rounded-lg flex items-center gap-4 animate-pulse"><XCircle size={24} /><p className="font-semibold">{error}</p></div>}
                {renderContent()}
                {/* --- FOOTER REMOVED FROM HERE --- */}
            </div>
        </div>
    );
}

function QRScannerScreen({ onScanSuccess, isScriptLoaded, onError, scanStatus, qrIconUrl }) {
    const scannerRef = useRef(null);

    const borderClasses = {
        idle: 'border-slate-200',
        success: 'border-green-500 shadow-lg shadow-green-500/50',
        error: 'border-red-500 shadow-lg shadow-red-500/50'
    };

    useEffect(() => {
        if (!isScriptLoaded || scannerRef.current || document.getElementById('qr-reader')?.innerHTML) return;
        const qrboxSize = window.innerWidth < 450 ? 250 : 350; 
        const scanner = new window.Html5QrcodeScanner('qr-reader', { fps: 10, qrbox: { width: qrboxSize, height: qrboxSize } }, false);
        
        const handleScan = async (decodedText) => {
            if (scannerRef.current?.getState() === 2) { // 2 is SCANNING state
                try {
                    scanner.pause(true);
                    const token = localStorage.getItem('token');
                    const res = await fetch(`${API_URL}/api/vote/validate-qr`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, body: JSON.stringify({ qrToken: decodedText }) });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.msg || 'QR validation failed');
                    onScanSuccess(data.qrCodeId);
                } catch (err) { 
                    onError(err.message); 
                    setTimeout(() => {
                        if (scannerRef.current?.getState() === 3) { // 3 is PAUSED state
                            scanner.resume();
                        }
                    }, 3000);
                }
            }
        };

        scanner.render(handleScan, (err) => {});
        scannerRef.current = scanner;
        
        return () => { 
            if (scannerRef.current && scannerRef.current.isScanning) {
                scannerRef.current.clear().catch(console.error);
            }
        };
    }, [isScriptLoaded, onScanSuccess, onError]);

    return (
        <Card>
            <div className="text-center">
                {qrIconUrl ? (
                    <img src={qrIconUrl} alt="QR Scanner Icon" className="mx-auto h-24 w-24 object-contain mb-2" onError={(e) => e.target.style.display='none'} />
                ) : (
                    <Vote size={48} className="mx-auto text-slate-700 mb-2" />
                )}
                <Header title="Scan QR Code" />
            </div>
            <p className="text-center text-slate-600 mb-4 -mt-6">Please scan the voter's unique QR code to begin.</p>
            {isScriptLoaded ? 
                <div id="qr-reader-container" className={`w-full max-w-[400px] mx-auto rounded-lg overflow-hidden border-8 transition-all duration-300 ${borderClasses[scanStatus]}`}>
                    <div id="qr-reader"></div>
                </div> 
                : 
                <div className="w-full h-[300px] flex items-center justify-center bg-slate-100 rounded-lg"><p>Loading Scanner...</p></div>
            }
        </Card>
    );
}

function VotingForm({ onVoteSubmit, qrCodeId, electionConfig }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [selections, setSelections] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const isReviewStep = currentStep === (electionConfig.positions || []).length;
    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/vote/cast`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, body: JSON.stringify({ qrCodeId, selections }) });
            if (!res.ok) { const data = await res.json(); throw new Error(data.msg || 'Failed to cast vote'); }
            onVoteSubmit();
        } catch (err) { alert(`Error: ${err.message}`); } finally { setIsLoading(false); }
    };
    if (isReviewStep) return <Card><Header title="Review Your Vote" onBack={() => setCurrentStep(currentStep - 1)} showBack={true} /><div className="space-y-4">{(electionConfig.positions || []).map(pos => { const candidate = (electionConfig.candidates || []).find(c => c._id === selections[pos.name]); return <div key={pos._id} className="p-3 bg-slate-50 rounded-lg flex justify-between items-center"><span className="font-bold text-slate-700">{pos.name}:</span><span className="text-slate-900">{candidate ? candidate.name : 'Not Selected'}</span></div>; })}</div><div className="mt-8"><Button className="w-full" onClick={handleSubmit} disabled={isLoading}>{isLoading ? 'Submitting...' : 'Confirm & Cast Vote'}</Button></div></Card>;
    const currentPosition = (electionConfig.positions || [])[currentStep];
    const candidatesForPosition = (electionConfig.candidates || []).filter(c => c.position === currentPosition.name);
    const handleSelect = (candidateId) => setSelections(prev => ({ ...prev, [currentPosition.name]: candidateId }));
    const handleNext = () => { if (selections[currentPosition.name]) setCurrentStep(currentStep + 1); };
    return <Card><Header title={`Vote for ${currentPosition.name}`} /><div className="space-y-4">{candidatesForPosition.map(c => <div key={c._id} onClick={() => handleSelect(c._id)} className={`p-4 border-2 rounded-lg flex items-center gap-4 cursor-pointer transition-all ${selections[currentPosition.name] === c._id ? 'border-slate-500 bg-slate-50' : 'border-slate-200 hover:border-slate-400'}`}><img src={c.photo || `https://placehold.co/100x100/E0E0E0/333?text=${c.name.charAt(0)}`} alt={c.name} className="w-16 h-16 rounded-full object-cover" /><span className="font-semibold text-lg text-slate-800">{c.name}</span>{selections[currentPosition.name] === c._id && <CheckCircle className="ml-auto text-slate-600" size={24} />}</div>)}</div><div className="mt-8"><Button className="w-full" onClick={handleNext} disabled={!selections[currentPosition.name]}>{currentStep === (electionConfig.positions || []).length - 1 ? 'Review Vote' : 'Next'}</Button></div><p className="mt-2 text-center text-sm text-slate-500">Step {currentStep + 1} of {(electionConfig.positions || []).length}</p></Card>;
}

function ThankYouScreen({ onFinish }) {
    useEffect(() => {
        const timer = setTimeout(onFinish, 3000);
        return () => clearTimeout(timer);
    }, [onFinish]);

    return (
        <Card className="text-center">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <h1 className="text-3xl font-bold">Thank You!</h1>
            <p className="text-slate-600 mt-2 text-lg">Your vote has been successfully recorded.</p>
            <p className="text-slate-500 mt-6">Returning to scanner...</p>
        </Card>
    );
}

function AutoScrollingList({ items, renderItem, maxHeight = '65vh', speed = 3 }) {
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const [isScrolling, setIsScrolling] = useState(false);

    useEffect(() => {
        if (containerRef.current && contentRef.current) {
            const needsScrolling = contentRef.current.scrollHeight > containerRef.current.clientHeight;
            setIsScrolling(needsScrolling);
        }
    }, [items, maxHeight]);

    if (!items || items.length === 0) {
        return <p className="text-center text-slate-500">No candidates found for this position.</p>;
    }

    const duration = items.length * speed;
    const animationStyle = { '--scroll-duration': `${duration}s` };

    return (
        <div ref={containerRef} style={{ maxHeight }} className="overflow-hidden relative">
            <div
                ref={contentRef}
                style={isScrolling ? animationStyle : {}}
                className={isScrolling ? 'animate-scroll' : ''}
            >
                <ul className="space-y-5 pb-5">
                    {items.map((item, index) => renderItem(item, index, 'original'))}
                </ul>
                {isScrolling && (
                    <ul className="space-y-5 pb-5">
                        {items.map((item, index) => renderItem(item, index, 'clone'))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function PublicResultsScreen() {
    const { data: results, loading: resultsLoading } = useApi('/public-results', 5000);
    const { data: config, loading: configLoading } = useApi('/election/config', 5000);
    const { data: adsData } = useApi('/ads', 10000); // Fetch ads every 10s
    
    const [currentPositionIndex, setCurrentPositionIndex] = useState(0);
    const [viewMode, setViewMode] = useState('initial');
    const [isAdVisible, setIsAdVisible] = useState(false);
    const [nextView, setNextView] = useState(null);
    const [adIndex, setAdIndex] = useState(0);
    const [showPopup, setShowPopup] = useState(true);

    const bannerAds = adsData ? adsData.filter(a => a.type === 'banner' && a.isActive) : [];
    const featuredAds = adsData ? adsData.filter(a => a.type === 'featured' && a.isActive) : [];

    useEffect(() => {
        if (bannerAds.length > 1) {
            const timer = setInterval(() => {
                setAdIndex(prev => (prev + 1) % bannerAds.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [bannerAds.length]);

    useEffect(() => {
        const hasSeenPopup = sessionStorage.getItem('hasSeenFeaturedAd');
        if (hasSeenPopup) setShowPopup(false);
    }, []);

    const dismissPopup = () => {
        setShowPopup(false);
        sessionStorage.setItem('hasSeenFeaturedAd', 'true');
    };
    const renderAdBanner = () => {
        if (bannerAds.length === 0) return null;
        const currentAd = bannerAds[adIndex];
        return (
            <div className="w-full bg-white border-t border-slate-200 p-3 text-center mt-auto shadow-[0_-4px_10px_rgba(0,0,0,0.05)] overflow-hidden relative h-48 flex items-center justify-center">
                <p className="absolute top-2 left-1/2 -translate-x-1/2 text-slate-400 text-[10px] uppercase tracking-widest font-bold z-10">Sponsored Advertisement</p>
                <AnimatePresence mode="wait">
                    <motion.a 
                        key={currentAd._id}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.5 }}
                        href={currentAd.targetUrl.match(/^https?:\/\//) ? currentAd.targetUrl : 'https://' + currentAd.targetUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-block transition-transform hover:scale-[1.02] absolute w-full h-full p-6 flex justify-center"
                    >
                        <img src={currentAd.imageUrl} alt="Advertisement" className="max-h-full max-w-full object-contain rounded-xl shadow-md" />
                    </motion.a>
                </AnimatePresence>
            </div>
        );
    };
    
    const renderFeaturedAds = () => {
        if (featuredAds.length === 0) return null;
        return (
            <div className="w-full mt-12 mb-8 bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-sm">
                <h3 className="text-slate-400 font-bold uppercase tracking-widest text-sm text-center mb-6 flex items-center justify-center gap-4">
                    <span className="w-8 h-[1px] bg-slate-300"></span>
                    Featured Sponsors
                    <span className="w-8 h-[1px] bg-slate-300"></span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-items-center">
                {featuredAds.map(ad => (
                    <a key={ad._id} href={ad.targetUrl.match(/^https?:\/\//) ? ad.targetUrl : 'https://' + ad.targetUrl} target="_blank" rel="noopener noreferrer" className="block w-full max-w-[200px] transition-transform hover:-translate-y-2 hover:shadow-xl rounded-2xl overflow-hidden shadow-md bg-white border border-slate-100">
                        <img src={ad.imageUrl} className="w-full h-auto object-cover" alt="Featured Sponsor" />
                    </a>
                ))}
                </div>
            </div>
        );
    };


    const isFinalPublished = config?.election?.resultsPublished;
    const electionStatus = config?.election?.status;

    const groupedResults = useMemo(() => {
        const grouped = (results || []).reduce((acc, candidate) => {
            (acc[candidate.position] = acc[candidate.position] || []).push(candidate);
            return acc;
        }, {});
        for (const position in grouped) {
            grouped[position].sort((a, b) => b.votes - a.votes);
        }
        return grouped;
    }, [results]);

    const totalVotes = useMemo(() => (results || []).reduce((sum, c) => sum + c.votes, 0), [results]);
    const positions = Object.keys(groupedResults);

    useEffect(() => {
        if (positions.length > 1 && (viewMode === 'live' || viewMode === 'final')) {
            const timer = setInterval(() => {
                setCurrentPositionIndex(prev => (prev + 1) % positions.length);
            }, 15000);
            return () => clearInterval(timer);
        }
    }, [positions.length, viewMode]);

    const handleShowResultsClick = (targetView) => {
        const viewCount = parseInt(sessionStorage.getItem('resultViewCount') || '0');
        
        if ((config?.desktopBannerUrl || config?.mobileBannerUrl) && viewCount < 2) {
            sessionStorage.setItem('resultViewCount', viewCount + 1);
            setNextView(targetView);
            setIsAdVisible(true);
        } else {
            setViewMode(targetView);
        }
    };

    const handleAdClose = () => {
        setIsAdVisible(false);
        if (nextView) {
            setViewMode(nextView);
            setNextView(null);
        }
    };

    if (configLoading) {
        return <Card><FancyLoader text="Loading Configuration..." /></Card>;
    }
    
    if (!config?.publicScreenEnabled) {
        return <div className="text-center w-full max-w-5xl mx-auto px-4 md:px-8"><Card><p className="text-xl font-semibold">The public results screen is not currently enabled by the administrator.</p></Card></div>
    }

    const renderFooter = () => (
       <footer className="text-center text-slate-500 text-sm mt-12 py-4">
            <p className="mb-2">
                Developed by <a href="https://finixia.in/" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">Finixia dedecons</a>
            </p>
            <a href="https://finixia.in/about-us" title="About Finixia dedecons" target="_blank" rel="noopener noreferrer" className="hover:underline">About Us</a>
            <span className="mx-2 text-slate-400">|</span>
            <a href="https://finixia.in/contact-us" title="Connect with Finixia dedecons" target="_blank" rel="noopener noreferrer" className="hover:underline">Contact Us</a>
       </footer>
    );

    const renderHeader = (title) => (
         <header className="text-center mb-8">
            <div className="flex justify-center items-center gap-4 mb-8">
                <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight drop-shadow-sm">{title}</h1>
                {viewMode === 'live' && <motion.div animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="bg-red-500 text-white px-4 py-1 rounded-full font-bold text-xl uppercase tracking-widest shadow-lg flex items-center gap-2"><div className="w-3 h-3 bg-white rounded-full animate-ping" /> LIVE</motion.div>}
            </div>
            <div className="logo-holder h-20 md:h-32 flex flex-col sm:flex-row justify-center items-center gap-6 md:gap-12">
               {config.logoUrl && <img src={config.logoUrl} alt="Election Logo" className="mx-auto max-h-full w-auto object-contain drop-shadow-md" />}
               {config.secondaryLogoUrl && <img src={config.secondaryLogoUrl} alt="Secondary Logo" className="mx-auto max-h-full w-auto object-contain drop-shadow-md" />}
            </div>
        </header>
    );
    if (viewMode === 'initial') {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <div className="flex-1 flex flex-col w-full max-w-5xl mx-auto px-4 md:px-8 pt-8 pb-4">
                    {renderHeader(`${config.appTitle || 'Finelectra'} Results`)}
                    
                    <div className="flex-1 flex flex-col items-center">
                        <AnimatePresence>
                        {isFinalPublished && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="w-full mb-8 overflow-hidden rounded-xl shadow-lg border border-green-200 bg-green-50">
                                <div className="flex items-center justify-center p-3 text-green-800 font-bold text-lg md:text-xl">
                                    <marquee scrollamount="8" className="w-full">
                                        🚀 The final election results have been published! View the official winners now.
                                    </marquee>
                                </div>
                            </motion.div>
                        )}
                        </AnimatePresence>
                        
                        <Card className="w-full bg-white border-slate-200 text-slate-800 shadow-xl rounded-3xl p-8 mb-8">
                            {electionStatus === 'Pending' ? (
                                <div className="text-center">
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        <AlertTriangle size={48} className="mx-auto text-yellow-500 mb-4" />
                                        <h2 className="text-3xl font-extrabold text-slate-800 mb-4">Election Not Yet Started</h2>
                                        <p className="text-slate-500 text-lg">The live voting results will appear here once the election begins. Please check back later.</p>
                                    </motion.div>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <h2 className="text-4xl font-extrabold text-slate-800 mb-4 tracking-tight">Welcome</h2>
                                    <p className="text-slate-500 text-xl mb-12">Select an option below to view the election results.</p>
                                    <div className="flex flex-col md:flex-row justify-center gap-4">
                                        <Button onClick={() => handleShowResultsClick('live')} className="w-full md:w-auto"><Activity size={16} /> Show Live Voting</Button>
                                        <AnimatePresence>
                                        {isFinalPublished && (
                                            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="w-full md:w-auto">
                                                <Button onClick={() => handleShowResultsClick('final')} variant="success" className="w-full"><Award size={16} /> Show Final Results</Button>
                                            </motion.div>
                                        )}
                                        </AnimatePresence>
                                    </div>
                                    {!isFinalPublished && (
                                        <div className="text-center mt-8 bg-blue-100 text-blue-800 p-3 rounded-lg shadow-sm flex items-center justify-center gap-3">
                                            <AlertTriangle size={20} />
                                            <p className="font-semibold">Final results are not yet published by the authority.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                        
                        {renderFeaturedAds()}
                    </div>
                    
                    {renderFooter()}
                </div>
                {renderAdBanner()}
            
            {showPopup && featuredAds.length > 0 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-2 overflow-hidden">
                        <button onClick={dismissPopup} className="absolute top-4 right-4 bg-slate-900 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold shadow hover:bg-slate-700 z-10">&times;</button>
                        <a href={featuredAds[0].targetUrl.match(/^https?:\/\//) ? featuredAds[0].targetUrl : 'https://' + featuredAds[0].targetUrl} target="_blank" rel="noopener noreferrer" onClick={dismissPopup}>
                            <img src={featuredAds[0].imageUrl} className="w-full h-auto max-h-[70vh] object-contain rounded-2xl" alt="Featured Sponsor" />
                        </a>
                    </motion.div>
                </div>
            )}
            </div>
        );
    }
    
    if (viewMode === 'live' && isFinalPublished) {
         return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <div className="flex-1 flex flex-col w-full max-w-5xl mx-auto px-4 md:px-8 pt-8 pb-4">
                    {renderHeader(`${config.appTitle || 'Finelectra'} Live Voting`)}
                    
                    <div className="flex-1 flex flex-col items-center">
                        <Card className="w-full text-center bg-white border-slate-200 text-slate-800 shadow-xl rounded-3xl p-8 mb-8">
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                                <Award size={48} className="mx-auto text-green-500 mb-4"/>
                                <h2 className="text-4xl font-extrabold text-slate-800 mb-6">The Results Are In!</h2>
                                <p className="text-slate-600 text-xl mb-8">
                                    <span className="spin-text-animation">The final results have been officially published.</span>
                                </p>
                                <p className="text-slate-600 text-lg mb-8">
                                    Please proceed to the final results page to view the winners.
                                </p>
                                <div className="flex flex-col sm:flex-row justify-center gap-4">
                                    <Button onClick={() => handleShowResultsClick('final')} variant="success" className="w-full sm:w-auto">
                                        <Award size={16} /> View Final Results
                                    </Button>
                                     <Button onClick={() => setViewMode('initial')} variant="secondary" className="w-full sm:w-auto">
                                        <ArrowLeft size={16} /> Back to Home
                                    </Button>
                                </div>
                            </motion.div>
                        </Card>
                        {renderFeaturedAds()}
                    </div>
                    {renderFooter()}
                </div>
                {renderAdBanner()}
            
            {showPopup && featuredAds.length > 0 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-2 overflow-hidden">
                        <button onClick={dismissPopup} className="absolute top-4 right-4 bg-slate-900 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold shadow hover:bg-slate-700 z-10">&times;</button>
                        <a href={featuredAds[0].targetUrl.match(/^https?:\/\//) ? featuredAds[0].targetUrl : 'https://' + featuredAds[0].targetUrl} target="_blank" rel="noopener noreferrer" onClick={dismissPopup}>
                            <img src={featuredAds[0].imageUrl} className="w-full h-auto max-h-[70vh] object-contain rounded-2xl" alt="Featured Sponsor" />
                        </a>
                    </motion.div>
                </div>
            )}
            </div>
        );
    }

    const currentPosition = positions[currentPositionIndex];
    const candidates = groupedResults[currentPosition];
    const maxVotes = Math.max(...(candidates || []).map(c => c.votes), 1);

    const renderCandidate = (c, index, keySuffix) => {
        const isWinner = index === 0 && c.votes > 0;
        const tagText = viewMode === 'final' ? 'WINNER' : 'LEADING';
        const tagBgColor = viewMode === 'final' ? 'bg-green-500 text-white border-2 border-white' : 'bg-yellow-500 text-white border-2 border-white';
        
        return (
            <motion.li initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} key={`${c._id}-${keySuffix}`} className="flex items-center gap-6 md:gap-8 bg-white p-4 md:p-6 rounded-2xl border border-slate-100 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="relative flex-shrink-0">
                    <img src={c.photo || `https://placehold.co/120x120/E0E0E0/333?text=${c.name.charAt(0)}`} alt={c.name} className="w-16 h-16 md:w-28 md:h-28 rounded-2xl object-cover shadow-lg border-4 border-white" />
                    {isWinner && (
                        <motion.div 
                            initial={{ scale: 0, rotate: -45 }}
                            animate={{ scale: 1, rotate: 12 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                            className={`absolute -top-3 -right-3 md:-top-4 md:-right-4 ${tagBgColor} text-xs md:text-base font-extrabold px-3 py-1 md:px-4 md:py-2 rounded-xl shadow-xl transform`}>
                            {tagText} 🏆
                        </motion.div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row justify-between sm:items-baseline mb-3">
                        <span className="text-xl md:text-3xl font-bold text-slate-800 truncate pr-4">{c.name}</span>
                        <span className="text-2xl md:text-4xl font-extrabold text-white bg-indigo-600 px-4 py-1 rounded-xl shadow-lg">{c.votes} <span className="text-lg md:text-xl text-indigo-200 font-semibold">Votes</span></span>
                    </div>
                    <AnimatedBar value={c.votes} maxValue={maxVotes} />
                </div>
            </motion.li>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col pt-8">
            <div className="flex-1 flex flex-col w-full max-w-5xl mx-auto px-4 md:px-8 pb-4">
            <AnimatePresence>
                {isAdVisible && (
                    <AdBannerModal
                        desktopBannerUrl={config.desktopBannerUrl}
                        mobileBannerUrl={config.mobileBannerUrl}
                        onClose={handleAdClose}
                    />
                )}
            </AnimatePresence>
            {renderHeader(`${config.appTitle || 'Finelectra'} ${viewMode === 'final' ? 'Final Results' : 'Live Results'}`)}
            
            <div className="flex justify-center mb-6">
                 <Button onClick={() => setViewMode('initial')} variant="secondary">
                     <ArrowLeft size={16} /> Back to Selection
                 </Button>
            </div>
            
            {resultsLoading && !results ? (
                 <Card><FancyLoader text="Loading Results..." /></Card>
            ) : positions.length === 0 ? (
                 <Card><p className="text-center text-xl">No results to display yet. Please wait for votes to be cast.</p></Card>
            ) : (
                <>
                    <Card className="mb-6 text-center !p-4">
                        <p className="text-slate-600 font-semibold uppercase tracking-wider text-sm">Total Votes Cast</p>
                        <p className="text-4xl md:text-5xl font-bold">{totalVotes}</p>
                    </Card>
                    <AnimatePresence mode="wait">
                         <motion.div
                            key={currentPositionIndex}
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -50 }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        >
                            <Card>
                                <h2 className="text-2xl md:text-3xl font-bold capitalize mb-6">{currentPosition}</h2>
                                <AutoScrollingList
                                    items={candidates}
                                    renderItem={renderCandidate}
                                    maxHeight="60vh"
                                />
                            </Card>
                        </motion.div>
                    </AnimatePresence>
                    {positions.length > 1 && (
                        <div className="flex justify-center gap-3 mt-6">
                            {positions.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPositionIndex(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentPositionIndex ? 'bg-slate-800 scale-125' : 'bg-slate-300 hover:bg-slate-400'}`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
            
            {renderFeaturedAds()}
            </div>
            
            {renderFooter()}
            {renderAdBanner()}
        </div>
    );
}


// --- Main App Component ---
const navigate = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
};

export default function App() {
    const [user, setUser] = useState(null);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const [electionConfig, setElectionConfig] = useState(null);
    const [ads, setAds] = useState([]);
    const [error, setError] = useState('');
    const [view, setView] = useState('main');

    const fetchConfig = async () => {
        try {
            const res = await fetch(`${API_URL}/api/election/config`);
            if (!res.ok) throw new Error('Could not load election data. Is the server running?');
            setElectionConfig(await res.json());
        } catch (err) {
            if (!error) setError(err.message);
        }
    };

    useEffect(() => {
        if (electionConfig && electionConfig.appTitle) {
            document.title = electionConfig.appTitle;
        }
    }, [electionConfig]);


    useEffect(() => {
        const handleRouting = () => {
            const path = window.location.pathname;
            if (path === '/login') {
                setView('login');
            } else if (path === '/public') {
                setView('public');
            } else if (path === '/apply') {
                setView('apply');
            
                            } else {
                setView('main');
            }
        };

        window.addEventListener('popstate', handleRouting);
        handleRouting();

        const verifyUser = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const res = await fetch(`${API_URL}/api/auth/verify`, { headers: { 'x-auth-token': token } });
                    if (res.ok) { setUser(await res.json()); } else { localStorage.removeItem('token'); }
                } catch (err) { localStorage.removeItem('token'); }
            }
        };
        verifyUser();
        const script = document.createElement('script');
        script.src = "https://unpkg.com/html5-qrcode";
        script.onload = () => setIsScriptLoaded(true);
        document.body.appendChild(script);
        fetchConfig();
        
        return () => { 
            if (document.body.contains(script)) { document.body.removeChild(script); }
            window.removeEventListener('popstate', handleRouting);
        };
    }, []);

    const handleLogin = (userData) => { setUser(userData); };
    const handleLogout = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        setUser(null);
        localStorage.removeItem('token');
    };
    
    const renderContent = () => {
        if (view === 'main') return <LandingPage electionConfig={electionConfig} />;
        if (view === 'apply') return <NominationForm electionConfig={electionConfig} />;
    if (view === 'public') {
            return <PublicResultsScreen />;
        }

        if (error) return <div className="max-w-md mx-auto"><Card><p className="text-red-500 text-center font-semibold">{error}</p></Card></div>;
        if (!electionConfig) return <div className="max-w-md mx-auto"><Card><FancyLoader text="Loading Election..." /></Card></div>;
        if (view === 'login' && !user) return <LoginScreen onLogin={handleLogin} logoUrl={electionConfig?.logoUrl} secondaryLogoUrl={electionConfig?.secondaryLogoUrl} appName={electionConfig?.appTitle} />;
        if (!user) return <div className="max-w-md mx-auto text-center"><Card><p>Please log in.</p><Button onClick={() => navigate('/login')}>Go to Login</Button></Card></div>; 
        if (user.role === 'admin') return <AdminDashboard user={user} onLogout={handleLogout} electionConfig={electionConfig} refetchElectionStatus={fetchConfig}/>;
        if (user.role === 'voter') {
            if (electionConfig.election.status !== 'Active') return <div className="max-w-md mx-auto"><Card><p className="text-center font-semibold text-lg">The election is not currently active.</p><p className="text-center mt-2">Current Status: {electionConfig.election.status}</p><div className="mt-6"><Button className="w-full" onClick={handleLogout}>Logout</Button></div></Card></div>
            return <MonitorScreen onLogout={handleLogout} isScriptLoaded={isScriptLoaded} electionConfig={electionConfig} refetchConfig={fetchConfig} />;
        }
    };
    return (
        <div className={view === 'main' ? "font-sans min-h-screen" : "bg-slate-100 min-h-screen font-sans flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-200"}>
            <GlobalStyle />
            <main className="w-full">{renderContent()}</main>
        </div>
    );
}